import React, { useState, useEffect } from "react";
import "./App.css";
import axios from "axios";

// Automatically detect backend URL from current domain - NEVER hardcode!
const getBackendUrl = () => {
  // If running locally in development
  if (window.location.hostname === 'localhost') {
    return process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
  }
  // In production, use the same origin (works for any domain)
  return window.location.origin;
};

const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

// Log the API URL for debugging (remove in production if needed)
console.log('API URL:', API);

// Header Component
const Header = ({ showShare = true }) => {
  const [showShareModal, setShowShareModal] = useState(false);
  const [copied, setCopied] = useState(false);
  const [shareUrl, setShareUrl] = useState("");

  useEffect(() => {
    setShareUrl(window.location.href);
  }, []);

  const handleCopy = () => {
    const textArea = document.createElement("textarea");
    textArea.value = shareUrl;
    document.body.appendChild(textArea);
    textArea.select();
    document.execCommand('copy');
    document.body.removeChild(textArea);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <React.Fragment>
      <header className="bg-slate-900/80 backdrop-blur-lg border-b border-white/10 px-4 py-3 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-4">
            <a href="https://companeze.com" className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 rounded-lg text-white text-sm font-medium transition-colors">
              ← Companeze.com
            </a>
            <a href="/" className="flex items-center gap-2">
              <div className="text-2xl font-bold text-white">Companeze</div>
              <div className="text-blue-400 text-lg">| BLDR</div>
            </a>
          </div>
          <nav className="flex items-center gap-2 sm:gap-4">
            <a href="/" className="text-white/70 hover:text-white text-xs sm:text-sm">Interview</a>
            <a href="/pricing" className="text-white/70 hover:text-white text-xs sm:text-sm">Pricing</a>
            <a href="/portal" className="text-white/70 hover:text-white text-xs sm:text-sm">Portal</a>
            <a href="/admin" className="text-white/70 hover:text-white text-xs sm:text-sm">Admin</a>
            {showShare && (
              <button onClick={() => setShowShareModal(true)} className="bg-blue-600 hover:bg-blue-700 text-white px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm">
                Share
              </button>
            )}
          </nav>
        </div>
      </header>
      {showShareModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-2xl p-6 max-w-md w-full">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-white">Share Companeze BLDR</h3>
              <button onClick={() => setShowShareModal(false)} className="text-white/60 hover:text-white text-2xl">&times;</button>
            </div>
            
            {/* Shareable Link Box */}
            <div className="mb-4">
              <label className="block text-white/70 text-sm mb-2">Share this link:</label>
              <div className="flex gap-2">
                <input 
                  type="text" 
                  readOnly 
                  value={shareUrl}
                  className="flex-1 px-3 py-2 rounded-lg bg-white/10 border border-white/20 text-white text-sm"
                />
                <button 
                  onClick={handleCopy}
                  className={`px-4 py-2 rounded-lg text-white text-sm ${copied ? 'bg-green-600' : 'bg-blue-600 hover:bg-blue-700'}`}
                >
                  {copied ? 'Copied!' : 'Copy'}
                </button>
              </div>
            </div>

            {/* Share Options */}
            <div className="grid grid-cols-1 gap-3 mb-4">
              <a 
                href={`https://wa.me/?text=${encodeURIComponent(`Check out Companeze BLDR for business planning: ${shareUrl}`)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-green-600 hover:bg-green-700 text-white py-3 rounded-lg text-center block"
              >
                Share via WhatsApp
              </a>
              <a 
                href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(`Check out Companeze BLDR for business planning: ${shareUrl}`)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-blue-500 hover:bg-blue-600 text-white py-3 rounded-lg text-center block"
              >
                Share on Twitter/X
              </a>
              <a 
                href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-blue-700 hover:bg-blue-800 text-white py-3 rounded-lg text-center block"
              >
                Share on LinkedIn
              </a>
            </div>

            {/* Manual Share Instructions */}
            <div className="bg-white/5 rounded-lg p-4 text-center">
              <p className="text-white/60 text-sm mb-2">Or send manually via SMS/Email:</p>
              <p className="text-white/80 text-xs">Copy the link above and paste it in your message</p>
            </div>

            <div className="mt-4 text-center">
              <a href="/terms" className="text-blue-400 hover:text-blue-300 text-sm">Terms & Conditions</a>
            </div>
          </div>
        </div>
      )}
    </React.Fragment>
  );
};

// Terms Page
const TermsPage = () => (
  <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
    <Header showShare={false} />
    <div className="max-w-4xl mx-auto py-12 px-4">
      <h1 className="text-3xl font-bold text-white mb-8">Terms & Conditions</h1>
      <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 text-white/80">
        <p className="mb-4">Welcome to Companeze BLDR. These terms outline the rules for using our services.</p>
        <h2 className="text-xl font-semibold text-white mt-6 mb-3">1. Services</h2>
        <p className="mb-4">Companeze provides business planning consultation, funding assistance, and related services.</p>
        <h2 className="text-xl font-semibold text-white mt-6 mb-3">2. Pricing</h2>
        <p className="mb-4">Our funding services are priced as flat rates plus a 5% success fee on capital raised.</p>
        <h2 className="text-xl font-semibold text-white mt-6 mb-3">3. Confidentiality</h2>
        <p className="mb-4">All business information shared is kept strictly confidential.</p>
      </div>
    </div>
  </div>
);

// Pricing Page Component
const PricingPage = () => {
  const plans = [
    {
      name: "Basic",
      monthly: 99,
      popular: false,
      description: "Perfect for getting started with business planning",
      features: [
        "Business Interview",
        "Business Planner",
        "Content Assistant",
        "Action List",
        "Basic Consultation"
      ]
    },
    {
      name: "Professional",
      monthly: 199,
      popular: true,
      description: "Complete business launch package",
      features: [
        "Everything in Basic",
        "Corporate Set Up Assistance",
        "Tax & License Guidance",
        "Compliance Review",
        "Team Evaluation",
        "Financial Planning Upgrade",
        "Digital Presence Review",
        "Priority Support"
      ]
    },
    {
      name: "Enterprise",
      monthly: 299,
      popular: false,
      description: "Most comprehensive standard plan",
      features: [
        "Everything in Professional",
        "Advanced Digital Packages",
        "Website Setup Assistance",
        "Social Media Setup",
        "Marketing Setup",
        "Basic Agreements",
        "Dedicated Account Manager",
        "Ongoing Maintenance Support"
      ]
    },
    {
      name: "Custom",
      monthly: null,
      popular: false,
      isCustom: true,
      description: "Tailored solutions for unique needs",
      features: [
        "All Enterprise Features",
        "Custom Development",
        "Dedicated Team",
        "Custom Integrations",
        "White Glove Service",
        "Custom Pricing Based on Scope"
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-900 via-blue-900 to-blue-900">
      <Header showShare={false} />
      <div className="max-w-6xl mx-auto px-4 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">Pricing Plans</h1>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Simple, transparent monthly pricing for your business needs
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {plans.map((plan, index) => (
            <div 
              key={plan.name}
              className={`relative rounded-2xl p-6 ${
                plan.popular 
                  ? 'bg-gradient-to-b from-blue-600 to-blue-800 border-2 border-blue-400 shadow-xl shadow-blue-500/20' 
                  : 'bg-gray-800 border border-gray-700'
              }`}
            >
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <span className="bg-yellow-500 text-black text-xs font-bold px-3 py-1 rounded-full">
                    MOST POPULAR
                  </span>
                </div>
              )}
              
              <h3 className="text-xl font-bold text-white mb-2">{plan.name}</h3>
              <p className="text-gray-300 text-sm mb-4">{plan.description}</p>
              
              {/* Pricing */}
              <div className="mb-6">
                {plan.isCustom ? (
                  <div className="text-3xl font-bold text-white">Contact Us</div>
                ) : (
                  <>
                    <div className="flex items-baseline">
                      <span className="text-4xl font-bold text-white">${plan.monthly}</span>
                      <span className="text-gray-400 ml-1">/month</span>
                    </div>
                    <p className="text-gray-400 text-sm mt-1">12-month commitment</p>
                  </>
                )}
              </div>

              {/* Features */}
              <ul className="space-y-3 mb-6">
                {plan.features.map((feature, i) => (
                  <li key={i} className="flex items-start text-sm">
                    <svg className={`w-5 h-5 mr-2 flex-shrink-0 ${plan.popular ? 'text-green-300' : 'text-green-500'}`} fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    <span className={plan.popular ? 'text-white' : 'text-gray-300'}>{feature}</span>
                  </li>
                ))}
              </ul>

              {/* CTA Button */}
              {plan.isCustom ? (
                <a 
                  href="mailto:info@companeze.com?subject=Custom%20Plan%20Inquiry"
                  className="block w-full text-center py-3 rounded-lg font-semibold bg-purple-600 text-white hover:bg-purple-700 transition-all"
                >
                  Contact Sales
                </a>
              ) : (
                <a 
                  href={`/payments?plan=${plan.name.toLowerCase().replace(/\s+/g, '_')}`}
                  className={`block w-full text-center py-3 rounded-lg font-semibold transition-all ${
                    plan.popular
                      ? 'bg-white text-blue-600 hover:bg-gray-100'
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                >
                  Get Started
                </a>
              )}
            </div>
          ))}
        </div>

        {/* Important Notice */}
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-6 mb-8">
          <h3 className="text-lg font-semibold text-yellow-400 mb-3">⚠️ Important Fee Information</h3>
          <p className="text-gray-300 text-sm leading-relaxed">
            Plans do not include external costs such as <strong>city, state, or federal fees</strong>, <strong>compliance costs</strong>, 
            <strong>licensing fees</strong>, or any other government-related fees. These fees are separate and will be charged 
            at the time of service delivery based on your specific business requirements and location.
          </p>
        </div>

        {/* Commitment Notice */}
        <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-6 text-center">
          <h3 className="text-lg font-semibold text-white mb-2">12-Month Commitment Required</h3>
          <p className="text-gray-400 text-sm max-w-2xl mx-auto">
            All plans are based on a 12-month commitment and must be on approved credit or other available financing plans. 
            Qualification is subject to our initial approval and application process.
          </p>
        </div>

        {/* Contact Section */}
        <div className="mt-12 text-center">
          <h3 className="text-2xl font-bold text-white mb-4">Questions About Pricing?</h3>
          <p className="text-gray-400 mb-6">Contact us to discuss your specific needs and find the right plan for you.</p>
          <a 
            href="mailto:info@companeze.com" 
            className="inline-block px-8 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white font-semibold rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all"
          >
            Contact Us
          </a>
        </div>
      </div>
    </div>
  );
};

// Payments Page Component
const PaymentsPage = () => {
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [customerInfo, setCustomerInfo] = useState({ name: "", email: "", phone: "" });
  const [error, setError] = useState(null);

  const plans = {
    basic: { name: "Basic", monthly: 99, description: "Interview, Planner, Content Assistant, Action List" },
    professional: { name: "Professional", monthly: 199, popular: true, description: "Complete business launch package" },
    enterprise: { name: "Enterprise", monthly: 299, description: "Most comprehensive standard plan" }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const planParam = params.get("plan");
    if (planParam && plans[planParam]) {
      setSelectedPlan(planParam);
    }
  }, []);

  const handleCheckout = async () => {
    if (!selectedPlan || !customerInfo.email || !customerInfo.name) {
      setError("Please fill in all required fields");
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.post(`${API}/payments/create-checkout`, {
        plan_id: selectedPlan,
        payment_type: "monthly",
        customer_email: customerInfo.email,
        customer_name: customerInfo.name,
        redirect_url: `${window.location.origin}/payment-success`
      });
      
      if (response.data.success && response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        setError("Failed to create checkout. Please try again.");
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Payment service unavailable. Please contact support.");
    } finally {
      setLoading(false);
    }
  };

  const currentPlan = selectedPlan ? plans[selectedPlan] : null;

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-900 via-blue-900 to-blue-900">
      <Header showShare={false} />
      <div className="max-w-2xl mx-auto px-4 py-12">
        <h1 className="text-3xl sm:text-4xl font-bold text-white text-center mb-2">Complete Your Purchase</h1>
        <p className="text-gray-400 text-center mb-8">Secure payment powered by Square</p>

        {error && (
          <div className="bg-red-500/20 border border-red-500 text-red-300 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {/* Plan Selection */}
        <div className="bg-gray-800 rounded-xl p-6 mb-6">
          <h2 className="text-lg font-semibold text-white mb-4">Select Your Plan</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {Object.entries(plans).map(([key, plan]) => (
              <button
                key={key}
                onClick={() => setSelectedPlan(key)}
                className={`p-4 rounded-lg border-2 text-left transition-all ${
                  selectedPlan === key
                    ? 'border-blue-500 bg-blue-500/20'
                    : 'border-gray-600 hover:border-gray-500 bg-gray-700/50'
                }`}
              >
                <div className="flex justify-between items-start">
                  <span className="font-medium text-white">{plan.name}</span>
                  {plan.popular && <span className="text-xs bg-yellow-500 text-black px-2 py-0.5 rounded">Popular</span>}
                </div>
                <div className="text-2xl font-bold text-white mt-2">${plan.monthly}<span className="text-sm text-gray-400">/mo</span></div>
                <div className="text-xs text-gray-400 mt-1">12-month commitment</div>
                <div className="text-xs text-gray-500 mt-2">{plan.description}</div>
              </button>
            ))}
          </div>
        </div>

        {/* 12-Month Commitment Notice */}
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4 mb-6">
          <p className="text-yellow-400 text-sm text-center">
            ⚠️ All plans require a 12-month commitment and approved credit
          </p>
        </div>

        {/* Customer Information */}
        <div className="bg-gray-800 rounded-xl p-6 mb-6">
          <h2 className="text-lg font-semibold text-white mb-4">Your Information</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">Full Name *</label>
              <input
                type="text"
                value={customerInfo.name}
                onChange={(e) => setCustomerInfo({ ...customerInfo, name: e.target.value })}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white focus:border-blue-500 focus:outline-none"
                placeholder="John Smith"
                required
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Email Address *</label>
              <input
                type="email"
                value={customerInfo.email}
                onChange={(e) => setCustomerInfo({ ...customerInfo, email: e.target.value })}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white focus:border-blue-500 focus:outline-none"
                placeholder="john@example.com"
                required
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Phone Number</label>
              <input
                type="tel"
                value={customerInfo.phone}
                onChange={(e) => setCustomerInfo({ ...customerInfo, phone: e.target.value })}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white focus:border-blue-500 focus:outline-none"
                placeholder="(555) 123-4567"
              />
            </div>
          </div>
        </div>

        {/* Order Summary */}
        {currentPlan && (
          <div className="bg-gray-800 rounded-xl p-6 mb-6">
            <h2 className="text-lg font-semibold text-white mb-4">Order Summary</h2>
            <div className="flex justify-between items-center py-3 border-b border-gray-700">
              <span className="text-gray-300">{currentPlan.name} Plan</span>
              <span className="text-white font-medium">${currentPlan.monthly}/mo × 12 months</span>
            </div>
            <div className="flex justify-between items-center py-3 border-b border-gray-700">
              <span className="text-gray-400 text-sm">Total over 12 months</span>
              <span className="text-gray-400">${currentPlan.monthly * 12}</span>
            </div>
            <div className="flex justify-between items-center py-3 text-lg">
              <span className="text-white font-semibold">Monthly Payment</span>
              <span className="text-white font-bold text-2xl">${currentPlan.monthly}/mo</span>
            </div>
          </div>
        )}

        {/* Checkout Button */}
        <button
          onClick={handleCheckout}
          disabled={loading || !selectedPlan || !customerInfo.email || !customerInfo.name}
          className="w-full py-4 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 disabled:from-gray-600 disabled:to-gray-600 text-white font-bold text-lg rounded-xl transition-all flex items-center justify-center gap-2"
        >
          {loading ? (
            <>
              <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              Processing...
            </>
          ) : (
            <>
              🔒 Proceed to Secure Checkout
            </>
          )}
        </button>

        <p className="text-gray-500 text-sm text-center mt-4">
          Payments are securely processed by Square. Your card information is never stored on our servers.
        </p>

        <div className="text-center mt-6">
          <a href="/pricing" className="text-blue-400 hover:text-blue-300">
            ← Back to Pricing
          </a>
        </div>
      </div>
    </div>
  );
};

// Payment Success Page
const PaymentSuccessPage = () => (
  <div className="min-h-screen bg-gradient-to-b from-blue-900 via-blue-900 to-blue-900 flex items-center justify-center px-4">
    <div className="bg-gray-800 rounded-2xl p-8 max-w-md w-full text-center">
      <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
        <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
        </svg>
      </div>
      <h1 className="text-2xl font-bold text-white mb-2">Payment Successful!</h1>
      <p className="text-gray-400 mb-6">
        Thank you for your purchase. We've sent a confirmation email with your receipt and next steps.
      </p>
      <p className="text-gray-300 mb-6">
        Our team will contact you within 24 hours to begin your business planning journey.
      </p>
      <a href="/" className="inline-block w-full py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg">
        Start Your Interview
      </a>
      <a href="/pricing" className="block mt-3 text-gray-400 hover:text-gray-300">
        View Other Plans
      </a>
    </div>
  </div>
);

// Client Interview Component
const ClientInterview = () => {
  const [budgetQuestions, setBudgetQuestions] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [answerModes, setAnswerModes] = useState({});
  const [loadingSuggestion, setLoadingSuggestion] = useState(false);
  const [clientInfo, setClientInfo] = useState({ name: "", email: "", phone: "" });
  const [budgetAnswers, setBudgetAnswers] = useState({});
  const [step, setStep] = useState("info");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [interviewId, setInterviewId] = useState(null);
  const [accessCode, setAccessCode] = useState(null);
  const [showSavedPrompt, setShowSavedPrompt] = useState(false);
  const [savedDraft, setSavedDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [autoFillNumbers, setAutoFillNumbers] = useState("");
  const [autoFilling, setAutoFilling] = useState(false);
  const [showAutoFill, setShowAutoFill] = useState(false);

  useEffect(() => {
    axios.get(`${API}/questions`).then(res => {
      setBudgetQuestions(res.data.budget_questions || []);
      setQuestions(res.data.questions || []);
      setLoading(false);
    }).catch(() => { setError("Failed to load questions."); setLoading(false); });
  }, []);

  // Check for saved progress when email changes
  const checkSavedProgress = async (email) => {
    if (!email) return;
    try {
      const res = await axios.get(`${API}/check-saved-progress/${encodeURIComponent(email)}`);
      if (res.data.has_saved_progress) {
        setSavedDraft(res.data.draft);
        setShowSavedPrompt(true);
      }
    } catch (e) { console.error("Error checking saved progress:", e); }
  };

  // Resume from saved draft
  const resumeFromDraft = () => {
    if (savedDraft) {
      setClientInfo({ name: savedDraft.client_name, email: savedDraft.client_email, phone: savedDraft.client_phone || "" });
      setAnswers(savedDraft.answers || {});
      setAnswerModes(savedDraft.answer_modes || {});
      setBudgetAnswers(savedDraft.budget_answers || {});
      setCurrentQuestion(savedDraft.current_question || 0);
      setShowSavedPrompt(false);
      setStep("interview");
    }
  };

  // Start fresh (clear saved progress)
  const startFresh = async () => {
    if (savedDraft?.client_email) {
      await axios.delete(`${API}/clear-saved-progress/${encodeURIComponent(savedDraft.client_email)}`);
    }
    setSavedDraft(null);
    setShowSavedPrompt(false);
  };

  // Save progress
  const saveProgress = async () => {
    if (!clientInfo.email) return;
    setSaving(true);
    try {
      await axios.post(`${API}/save-progress`, {
        client_name: clientInfo.name,
        client_email: clientInfo.email,
        client_phone: clientInfo.phone,
        answers,
        answer_modes: answerModes,
        budget_answers: budgetAnswers,
        current_question: currentQuestion
      });
      setLastSaved(new Date().toLocaleTimeString());
    } catch (e) { console.error("Error saving progress:", e); }
    setSaving(false);
  };

  // Auto-fill answers for specified question numbers
  const handleAutoFill = async () => {
    if (!autoFillNumbers.trim()) return;
    
    // Check if first 10 questions have answers
    const first10Count = Object.keys(answers).filter(k => {
      const num = parseInt(k.replace('q', ''));
      return num <= 10 && answers[k];
    }).length;
    
    if (first10Count < 5) {
      alert("Please answer at least 5 of the first 10 questions first. This helps us understand your business to generate better automated answers.");
      return;
    }
    
    setAutoFilling(true);
    try {
      const res = await axios.post(`${API}/auto-fill-answers`, {
        question_numbers: autoFillNumbers,
        client_name: clientInfo.name,
        existing_answers: answers
      });
      
      if (res.data.success) {
        setAnswers(prev => ({ ...prev, ...res.data.generated_answers }));
        // Set mode to "combine" for auto-filled answers
        const newModes = {};
        Object.keys(res.data.generated_answers).forEach(k => { newModes[k] = "combine"; });
        setAnswerModes(prev => ({ ...prev, ...newModes }));
        alert(`Successfully auto-filled ${res.data.questions_filled} questions!`);
        setShowAutoFill(false);
        setAutoFillNumbers("");
      } else {
        alert(res.data.error || "Failed to auto-fill answers");
      }
    } catch (e) { 
      console.error("Error auto-filling:", e); 
      alert("Failed to auto-fill answers. Please try again.");
    }
    setAutoFilling(false);
  };

  const handleModeChange = async (questionId, mode) => {
    setAnswerModes(prev => ({ ...prev, [questionId]: mode }));
    if (mode === "suggest") {
      setLoadingSuggestion(true);
      try {
        const q = questions.find(x => x.id === questionId);
        const res = await axios.post(`${API}/suggest-answer`, {
          question: q.question, section: q.section, client_name: clientInfo.name,
          context: Object.entries(answers).map(([k,v]) => { const x = questions.find(y => y.id === k); return x ? `${x.question}: ${v}` : ''; }).filter(Boolean).join('\n')
        });
        setAnswers(prev => ({ ...prev, [questionId]: res.data.suggestion }));
      } catch(e) { console.error(e); }
      setLoadingSuggestion(false);
    }
  };

  const handleSubmit = async () => {
    setStep("submitting");
    const allAnswers = [...budgetQuestions.map(q => ({ question_id: q.id, question: q.question, answer: budgetAnswers[q.id] || "", section: q.section, mode: "self" })),
      ...questions.map(q => ({ question_id: q.id, question: q.question, answer: answers[q.id] || "", section: q.section, mode: answerModes[q.id] || "self" }))];
    try {
      const res = await axios.post(`${API}/interviews`, { client_name: clientInfo.name, client_email: clientInfo.email, client_phone: clientInfo.phone, answers: allAnswers });
      setInterviewId(res.data.interview_id);
      setAccessCode(res.data.access_code);
      setStep("complete");
    } catch(e) { setError("Failed to submit."); setStep("interview"); }
  };

  if (loading) return <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900"><Header /><div className="flex items-center justify-center py-20 text-white text-xl">Loading Planner...</div></div>;
  if (error) return <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900"><Header /><div className="flex items-center justify-center py-20 text-red-400 text-xl">{error}</div></div>;

  if (step === "info") return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
      <Header />
      <div className="py-12 px-4"><div className="max-w-2xl mx-auto">
        <div className="text-center mb-8"><h1 className="text-3xl font-bold text-white mb-2">Planner</h1><p className="text-xl text-blue-200">Comprehensive Business Planning</p></div>
        
        {/* Resume from saved progress prompt */}
        {showSavedPrompt && savedDraft && (
          <div className="bg-amber-500/20 border border-amber-400/30 rounded-2xl p-6 mb-6">
            <h3 className="text-lg font-semibold text-amber-300 mb-2">Welcome Back!</h3>
            <p className="text-white/80 mb-4">We found your saved progress from {new Date(savedDraft.updated_at).toLocaleDateString()}.</p>
            <p className="text-white/60 text-sm mb-4">You answered {Object.keys(savedDraft.answers || {}).length} questions and were on question {(savedDraft.current_question || 0) + 1}.</p>
            <div className="flex gap-3">
              <button onClick={resumeFromDraft} className="px-6 py-2 bg-amber-500 hover:bg-amber-600 text-white font-medium rounded-lg">Resume</button>
              <button onClick={startFresh} className="px-6 py-2 bg-white/20 hover:bg-white/30 text-white rounded-lg">Start Fresh</button>
            </div>
          </div>
        )}
        
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 shadow-2xl">
          <h2 className="text-2xl font-semibold text-white mb-6">Welcome to Your Business Consultation</h2>
          <p className="text-blue-200 mb-4">This comprehensive interview will help us understand your business and create a detailed plan.</p>
          <div className="bg-blue-500/20 rounded-lg p-4 mb-6"><p className="text-blue-200 text-sm"><strong>What to expect:</strong> {questions.length}+ detailed questions covering financials, marketing, operations, and digital strategy.</p></div>
          <form onSubmit={(e) => { e.preventDefault(); if(clientInfo.name && clientInfo.email) setStep("budget"); }} className="space-y-6">
            <div><label className="block text-white mb-2">Full Name *</label><input type="text" required value={clientInfo.name} onChange={(e) => setClientInfo(prev => ({ ...prev, name: e.target.value }))} className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white" placeholder="Enter your full name" /></div>
            <div><label className="block text-white mb-2">Email Address *</label><input type="email" required value={clientInfo.email} onChange={(e) => { setClientInfo(prev => ({ ...prev, email: e.target.value })); }} onBlur={(e) => checkSavedProgress(e.target.value)} className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white" placeholder="Enter your email" /></div>
            <div><label className="block text-white mb-2">Phone Number</label><input type="tel" value={clientInfo.phone} onChange={(e) => setClientInfo(prev => ({ ...prev, phone: e.target.value }))} className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white" placeholder="Enter your phone" /></div>
            <button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-4 rounded-lg">Begin Consultation</button>
          </form>
        </div>
      </div></div>
    </div>
  );

  if (step === "budget") return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
      <Header />
      <div className="py-12 px-4"><div className="max-w-2xl mx-auto">
        <div className="text-center mb-8"><h1 className="text-2xl font-bold text-white mb-2">Budget Assessment</h1></div>
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 shadow-2xl">
          <h2 className="text-xl font-semibold text-white mb-6">Let's Start With Your Budget</h2>
          <div className="space-y-6">
            {budgetQuestions.map((q) => (
              <div key={q.id}>
                <label className="block text-white mb-3 font-medium">{q.question}</label>
                {q.options ? (
                  <div className="grid grid-cols-1 gap-2">
                    {q.options.map((opt, i) => (
                      <label key={i} className={`flex items-center gap-3 p-4 rounded-lg cursor-pointer transition-all ${budgetAnswers[q.id] === opt ? "bg-blue-600 text-white ring-2 ring-blue-400" : "bg-white/10 text-white/80 hover:bg-white/20"}`}>
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${budgetAnswers[q.id] === opt ? "border-white bg-white" : "border-white/50"}`}>
                          {budgetAnswers[q.id] === opt && <div className="w-2.5 h-2.5 rounded-full bg-blue-600"></div>}
                        </div>
                        <input type="radio" name={q.id} value={opt} checked={budgetAnswers[q.id] === opt} onChange={(e) => setBudgetAnswers(prev => ({ ...prev, [q.id]: e.target.value }))} className="sr-only" />
                        <span>{opt}</span>
                      </label>
                    ))}
                  </div>
                ) : <input type="text" value={budgetAnswers[q.id] || ""} onChange={(e) => setBudgetAnswers(prev => ({ ...prev, [q.id]: e.target.value }))} className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white" />}
              </div>
            ))}
          </div>
          <div className="mt-8 flex justify-between">
            <button onClick={() => setStep("info")} className="px-6 py-3 bg-white/20 text-white rounded-lg hover:bg-white/30">Back</button>
            <button onClick={() => setStep("interview")} className="px-8 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg">Continue to Interview</button>
          </div>
        </div>
      </div></div>
    </div>
  );

  if (step === "complete") return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
      <Header />
      <div className="flex items-center justify-center py-12 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-12 shadow-2xl">
            <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6"><svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg></div>
            <h2 className="text-3xl font-bold text-white mb-4">Thank You!</h2>
            <p className="text-xl text-blue-200 mb-6">We will contact you based on your answers.</p>
            
            {/* Client Portal Access Card */}
            {accessCode && (
              <div className="bg-amber-500/20 border border-amber-400/30 rounded-lg p-6 mb-6 text-left">
                <h3 className="text-lg font-semibold text-amber-300 mb-3">Your Client Portal Access</h3>
                <p className="text-white/80 text-sm mb-2">Save these credentials to access your documents anytime:</p>
                <div className="bg-black/20 rounded-lg p-4 space-y-2">
                  <p className="text-white/90"><span className="text-white/60">Email:</span> <span className="font-medium">{clientInfo.email}</span></p>
                  <p className="text-white/90"><span className="text-white/60">Access Code:</span> <span className="font-mono text-lg bg-amber-500/30 px-2 py-1 rounded">{accessCode}</span></p>
                </div>
                <a href="/portal" className="inline-block mt-4 bg-amber-500 hover:bg-amber-600 text-white font-medium px-6 py-2 rounded-lg text-sm">Go to Client Portal</a>
              </div>
            )}
            
            <div className="bg-white/5 rounded-lg p-6 mb-6">
              <h3 className="text-lg font-semibold text-white mb-2">Knowledge Center</h3>
              <p className="text-white/70 text-sm mb-4">Your business plan is being prepared.</p>
              <a href={`/knowledge/${interviewId}`} className="inline-block bg-blue-600 hover:bg-blue-700 text-white font-medium px-6 py-3 rounded-lg">View Your Summary</a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  if (step === "submitting") return <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900"><Header /><div className="flex items-center justify-center py-20"><div className="text-center"><div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-400 mx-auto mb-4"></div><p className="text-white text-xl">Submitting...</p></div></div></div>;

  const currentQ = questions[currentQuestion];
  const progress = ((currentQuestion + 1) / questions.length) * 100;
  const sections = [...new Set(questions.map(q => q.section))];
  const currentMode = answerModes[currentQ.id] || "self";
  const answeredCount = Object.keys(answers).filter(k => answers[k]).length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
      <Header />
      <div className="py-6 px-4"><div className="max-w-4xl mx-auto">
        {/* Progress Bar & Save Button */}
        <div className="mb-6">
          <div className="flex justify-between items-center text-sm text-white/70 mb-2">
            <span>Question {currentQuestion + 1} of {questions.length} ({answeredCount} answered)</span>
            <div className="flex items-center gap-3">
              {lastSaved && <span className="text-green-400 text-xs">Saved {lastSaved}</span>}
              <button onClick={saveProgress} disabled={saving} className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white text-xs rounded-lg flex items-center gap-1">
                {saving ? "Saving..." : "💾 Save Progress"}
              </button>
              <button onClick={() => setShowAutoFill(!showAutoFill)} className="px-3 py-1 bg-purple-600 hover:bg-purple-700 text-white text-xs rounded-lg">
                ⚡ Auto-Fill
              </button>
            </div>
          </div>
          <div className="w-full bg-white/20 rounded-full h-2"><div className="bg-blue-500 h-2 rounded-full transition-all" style={{ width: `${progress}%` }}></div></div>
        </div>
        
        {/* Auto-Fill Panel */}
        {showAutoFill && (
          <div className="mb-4 bg-purple-500/20 border border-purple-400/30 rounded-xl p-4">
            <h3 className="text-white font-semibold mb-2">⚡ Auto-Fill Questions</h3>
            <p className="text-purple-200 text-sm mb-3">
              <strong>Important:</strong> Answer the first 10 questions manually to help us understand your business. Then we can auto-generate answers for remaining questions.
            </p>
            
            {/* Complete All Unanswered Button */}
            <div className="mb-4 p-3 bg-gradient-to-r from-green-600/30 to-emerald-600/30 rounded-lg border border-green-400/30">
              <p className="text-green-200 text-sm mb-2">🚀 <strong>Quick Complete:</strong> Let AI answer all unanswered questions (except first 10)</p>
              <button 
                onClick={async () => {
                  const unanswered = questions.filter((q, idx) => idx >= 10 && !answers[q.id]).map((q, idx) => idx + 11);
                  if (unanswered.length === 0) { alert("All questions are answered!"); return; }
                  const first10Count = Object.keys(answers).filter(k => { const n = parseInt(k.replace('q','')); return n <= 10 && answers[k]; }).length;
                  if (first10Count < 5) { alert("Please answer at least 5 of the first 10 questions first."); return; }
                  setAutoFillNumbers(unanswered.join(","));
                  setAutoFilling(true);
                  try {
                    const res = await axios.post(`${API}/auto-fill-answers`, { question_numbers: unanswered.join(","), client_name: clientInfo.name, existing_answers: answers });
                    if (res.data.success) {
                      setAnswers(prev => ({ ...prev, ...res.data.generated_answers }));
                      const newModes = {}; Object.keys(res.data.generated_answers).forEach(k => { newModes[k] = "combine"; });
                      setAnswerModes(prev => ({ ...prev, ...newModes }));
                      alert(`✅ Completed ${res.data.questions_filled} questions!`);
                    } else { alert(res.data.error || "Failed"); }
                  } catch(e) { alert("Error: " + e.message); }
                  setAutoFilling(false);
                }}
                disabled={autoFilling}
                className="w-full px-4 py-3 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-semibold rounded-lg disabled:opacity-50"
              >
                {autoFilling ? "⏳ AI is completing all questions..." : "🤖 Complete All Unanswered Questions with AI"}
              </button>
            </div>
            
            <div className="flex gap-2 mb-3">
              <input 
                type="text" 
                value={autoFillNumbers} 
                onChange={(e) => setAutoFillNumbers(e.target.value)}
                placeholder="e.g., 11-20, 25, 30-50" 
                className="flex-1 px-3 py-2 rounded-lg bg-white/10 border border-white/20 text-white text-sm"
              />
              <button 
                onClick={handleAutoFill} 
                disabled={autoFilling || !autoFillNumbers.trim()}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm rounded-lg disabled:opacity-50"
              >
                {autoFilling ? "Generating..." : "Generate"}
              </button>
            </div>
            <p className="text-white/50 text-xs">Or enter specific question numbers (e.g., "11-50" or "15, 20-30, 45")</p>
            <div className="mt-2 flex gap-2 flex-wrap">
              <button onClick={() => setAutoFillNumbers("11-50")} className="px-2 py-1 bg-white/10 text-white/70 text-xs rounded hover:bg-white/20">11-50</button>
              <button onClick={() => setAutoFillNumbers("51-100")} className="px-2 py-1 bg-white/10 text-white/70 text-xs rounded hover:bg-white/20">51-100</button>
              <button onClick={() => setAutoFillNumbers("101-150")} className="px-2 py-1 bg-white/10 text-white/70 text-xs rounded hover:bg-white/20">101-150</button>
              <button onClick={() => setAutoFillNumbers("11-150")} className="px-2 py-1 bg-white/10 text-white/70 text-xs rounded hover:bg-white/20">All (11-150)</button>
            </div>
          </div>
        )}
        
        {/* Section Tabs */}
        <div className="mb-4 overflow-x-auto"><div className="flex gap-2 pb-2 min-w-max">
          {sections.map((s, i) => (<button key={s} onClick={() => { const idx = questions.findIndex(q => q.section === s); if(idx !== -1) setCurrentQuestion(idx); }} className={`px-3 py-1 rounded-full text-xs whitespace-nowrap ${questions[currentQuestion].section === s ? "bg-blue-500 text-white" : "bg-white/10 text-white/50 hover:bg-white/20"}`}>{s}</button>))}
        </div></div>
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 shadow-2xl">
          <div className="mb-2 text-blue-300 text-sm font-medium">{currentQ.section}</div>
          <h2 className="text-lg font-semibold text-white mb-4">{currentQ.question}</h2>
          {currentQ.type === "select" && currentQ.options ? (
            <div className="grid grid-cols-1 gap-2">{currentQ.options.map((opt, i) => (<label key={i} className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all ${answers[currentQ.id] === opt ? "bg-blue-600 text-white ring-2 ring-blue-400" : "bg-white/10 text-white/80 hover:bg-white/20"}`}><div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${answers[currentQ.id] === opt ? "border-white bg-white" : "border-white/50"}`}>{answers[currentQ.id] === opt && <div className="w-2.5 h-2.5 rounded-full bg-blue-600"></div>}</div><input type="radio" name={currentQ.id} value={opt} checked={answers[currentQ.id] === opt} onChange={(e) => setAnswers(prev => ({ ...prev, [currentQ.id]: e.target.value }))} className="sr-only" /><span>{opt}</span></label>))}</div>
          ) : currentQ.type === "textarea" ? (
            <textarea value={answers[currentQ.id] || ""} onChange={(e) => setAnswers(prev => ({ ...prev, [currentQ.id]: e.target.value }))} className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white min-h-[100px]" placeholder={currentMode === "build" ? "We will build this..." : "Enter your answer..."} disabled={currentMode === "build"} />
          ) : (
            <input type="text" value={answers[currentQ.id] || ""} onChange={(e) => setAnswers(prev => ({ ...prev, [currentQ.id]: e.target.value }))} className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white" placeholder={currentMode === "build" ? "We will build this..." : "Enter your answer..."} disabled={currentMode === "build"} />
          )}
          <div className="mt-4 p-3 bg-white/5 rounded-lg">
            <p className="text-white/70 text-xs mb-2">How would you like to answer?</p>
            <div className="flex flex-wrap gap-2">
              {["self", "combine", "suggest", "build"].map(m => (<label key={m} className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer text-sm ${currentMode === m ? (m === "self" ? "bg-blue-600" : m === "combine" ? "bg-amber-600" : m === "suggest" ? "bg-green-600" : "bg-purple-600") + " text-white" : "bg-white/10 text-white/70 hover:bg-white/20"}`}><input type="radio" name={`mode-${currentQ.id}`} checked={currentMode === m} onChange={() => handleModeChange(currentQ.id, m)} className="sr-only" /><span>{m === "self" ? "Answer myself" : m === "combine" ? "Combine & Refine" : m === "suggest" ? (loadingSuggestion ? "Loading..." : "Smart Suggest") : "We build it"}</span></label>))}
            </div>
            {currentMode === "combine" && <p className="text-amber-300/70 text-xs mt-2">Your answer will be enhanced with professional content and visual refinements</p>}
          </div>
          <div className="flex justify-between mt-6">
            <button onClick={() => currentQuestion > 0 && setCurrentQuestion(prev => prev - 1)} disabled={currentQuestion === 0} className={`px-5 py-2 rounded-lg font-medium ${currentQuestion === 0 ? "bg-white/10 text-white/30" : "bg-white/20 text-white hover:bg-white/30"}`}>Previous</button>
            {currentQuestion === questions.length - 1 ? (<button onClick={handleSubmit} className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg">Submit</button>) : (<button onClick={() => setCurrentQuestion(prev => prev + 1)} className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg">Next</button>)}
          </div>
        </div>
        <div className="mt-4 p-3 bg-white/5 rounded-lg"><p className="text-white/50 text-xs mb-2">Quick Navigation:</p><div className="flex flex-wrap gap-1">{questions.map((q, i) => (<button key={q.id} onClick={() => setCurrentQuestion(i)} className={`w-7 h-7 rounded text-xs font-medium ${i === currentQuestion ? "bg-blue-500 text-white" : answers[q.id] ? "bg-green-500/50 text-white" : "bg-white/10 text-white/50 hover:bg-white/20"}`}>{i + 1}</button>))}</div></div>
      </div></div>
    </div>
  );
};

// Knowledge Center
const KnowledgeCenter = ({ interviewId }) => {
  const [interview, setInterview] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/knowledge/${interviewId}`).then(res => { setInterview(res.data.interview); setSummary(res.data.summary); setLoading(false); }).catch(() => setLoading(false));
  }, [interviewId]);

  if (loading) return <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center text-white">Loading...</div>;
  if (!interview) return <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center text-white">Interview Not Found</div>;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-8"><h1 className="text-3xl font-bold text-white mb-2">Knowledge Center</h1><p className="text-blue-200">We build the answer for you</p></div>
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 mb-6"><h2 className="text-xl font-semibold text-white mb-2">Welcome, {interview.client_name}</h2><p className="text-white/70">Your personalized business insights.</p></div>
        {summary ? (<div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 mb-6"><h3 className="text-xl font-semibold text-white mb-4">Your Business Summary</h3><pre className="whitespace-pre-wrap text-white/90 font-sans text-sm leading-relaxed bg-white/5 rounded-lg p-6">{summary.content}</pre></div>) : (<div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 mb-6 text-center"><h3 className="text-xl font-semibold text-white mb-2">Your Business Plan is Being Prepared</h3><p className="text-white/70">Check back soon or we will contact you when ready.</p></div>)}
      </div>
    </div>
  );
};

// Admin Dashboard
const AdminDashboard = ({ onLogout }) => {
  const [interviews, setInterviews] = useState([]);
  const [selectedInterview, setSelectedInterview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [generatingGuide, setGeneratingGuide] = useState(false);
  const [generatingActions, setGeneratingActions] = useState(false);
  const [generatingAll, setGeneratingAll] = useState(false);
  const [view, setView] = useState("detail");
  const [showMobileDetail, setShowMobileDetail] = useState(false);
  const [adminTab, setAdminTab] = useState("interviews");
  const [messages, setMessages] = useState([]);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [sendingReply, setSendingReply] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editedAnswers, setEditedAnswers] = useState({});
  const [editNotes, setEditNotes] = useState("");
  const [sendingToClient, setSendingToClient] = useState(false);
  const [savingAnswers, setSavingAnswers] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);
  const [shareInfo, setShareInfo] = useState(null);
  
  const handleSendToClient = async (method) => {
    setSendingToClient(true);
    try {
      const res = await axios.post(`${API}/admin/interviews/${selectedInterview.id}/send-to-client`, { method });
      setShareInfo(res.data);
      if (method === 'link') {
        // Copy share text to clipboard
        navigator.clipboard.writeText(res.data.share_text);
        alert("Share information copied to clipboard!");
      } else {
        alert(res.data.message);
      }
    } catch(e) { 
      alert("Error: " + (e.response?.data?.detail || e.message)); 
    }
    setSendingToClient(false);
  };

  const fetchInterviews = () => axios.get(`${API}/admin/interviews`).then(res => { setInterviews(res.data.interviews); setLoading(false); }).catch(() => setLoading(false));
  const fetchMessages = () => axios.get(`${API}/admin/messages`).then(res => { setMessages(res.data.messages || []); }).catch(() => {});
  
  useEffect(() => { fetchInterviews(); fetchMessages(); }, []);
  
  // Initialize edited answers when entering edit mode
  const startEditMode = () => {
    if (selectedInterview?.answers) {
      const answersObj = {};
      selectedInterview.answers.forEach(a => { answersObj[a.question_id] = a.answer || ''; });
      setEditedAnswers(answersObj);
    }
    setEditMode(true);
  };
  
  // Save edited answers
  const saveEditedAnswers = async () => {
    setSavingAnswers(true);
    try {
      const updatedAnswers = selectedInterview.answers.map(a => ({
        ...a,
        answer: editedAnswers[a.question_id] !== undefined ? editedAnswers[a.question_id] : a.answer
      }));
      await axios.put(`${API}/admin/interviews/${selectedInterview.id}/answers`, { answers: updatedAnswers });
      alert("Answers saved successfully!");
      setEditMode(false);
      viewInterview(selectedInterview.id);
    } catch(e) { alert("Failed to save: " + e.message); }
    setSavingAnswers(false);
  };
  
  const viewInterview = (id) => { 
    axios.get(`${API}/admin/interviews/${id}`).then(res => { 
      setSelectedInterview(res.data); 
      setView("detail"); 
      setShowMobileDetail(true); // Open mobile modal
    }); 
  };
  const closeMobileDetail = () => {
    setShowMobileDetail(false);
  };
  const generatePlan = async (id) => { setGenerating(true); try { const res = await axios.post(`${API}/admin/interviews/${id}/generate-plan`); setSelectedInterview(prev => ({ ...prev, status: "completed", business_plan: res.data.business_plan, ai_research: res.data.ai_research })); fetchInterviews(); } catch(e) { alert("Failed"); } setGenerating(false); };
  const generateAdminGuide = async (id) => { setGeneratingGuide(true); try { const res = await axios.post(`${API}/admin/interviews/${id}/generate-admin-guide`); setSelectedInterview(prev => ({ ...prev, admin_guide: res.data.admin_guide })); alert("Admin Implementation Guide generated!"); } catch(e) { alert("Failed - ensure business plan is generated first"); } setGeneratingGuide(false); };
  const generateActionList = async (id) => { setGeneratingActions(true); try { const res = await axios.post(`${API}/admin/interviews/${id}/generate-action-list`); setSelectedInterview(prev => ({ ...prev, action_list: res.data.action_list })); alert("Action List generated!"); } catch(e) { alert("Failed to generate action list"); } setGeneratingActions(false); };
  const generateAllDocs = async (id) => { setGeneratingAll(true); try { await axios.post(`${API}/admin/interviews/${id}/generate-all-documents`); alert("All documents are being generated. This may take a few minutes. Refresh to see updates."); } catch(e) { alert("Failed to start generation"); } setGeneratingAll(false); };
  const generateSummary = async (id) => { try { await axios.post(`${API}/knowledge/${id}/generate-summary`); alert("Summary generated!"); viewInterview(id); } catch(e) { alert("Failed"); } };
  const deleteInterview = async (id) => { if (!window.confirm("Delete?")) return; try { await axios.delete(`${API}/admin/interviews/${id}`); fetchInterviews(); if (selectedInterview?.id === id) { setSelectedInterview(null); setShowMobileDetail(false); } } catch(e) {} };
  const downloadPlan = () => { if (!selectedInterview?.business_plan?.content) return; const blob = new Blob([`BUSINESS PLAN - CLIENT DELIVERABLE\n${selectedInterview.client_name}\nGenerated: ${new Date().toLocaleDateString()}\n\n${selectedInterview.business_plan.content}\n\n${"=".repeat(60)}\nRESEARCH & ANALYSIS\n${"=".repeat(60)}\n${selectedInterview.ai_research?.content || ""}`], { type: "text/plain" }); const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `Business_Plan_${selectedInterview.client_name.replace(/\s+/g, "_")}.txt`; a.click(); };
  const downloadAdminGuide = () => { if (!selectedInterview?.admin_guide?.content) return; const blob = new Blob([`COMPANEZE INTERNAL - ADMIN IMPLEMENTATION GUIDE\n${"=".repeat(60)}\nClient: ${selectedInterview.client_name}\nEmail: ${selectedInterview.client_email}\nGenerated: ${new Date().toLocaleDateString()}\n${"=".repeat(60)}\n\n${selectedInterview.admin_guide.content}`], { type: "text/plain" }); const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `ADMIN_Guide_${selectedInterview.client_name.replace(/\s+/g, "_")}.txt`; a.click(); };
  const downloadActionList = () => { if (!selectedInterview?.action_list?.content) return; const blob = new Blob([`ACTION ITEMS & TO-DO LIST\n${"=".repeat(60)}\nClient: ${selectedInterview.client_name}\nGenerated: ${new Date().toLocaleDateString()}\n${"=".repeat(60)}\n\n${selectedInterview.action_list.content}`], { type: "text/plain" }); const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `Action_List_${selectedInterview.client_name.replace(/\s+/g, "_")}.txt`; a.click(); };
  const downloadAllDocs = () => {
    if (!selectedInterview) return;
    const docs = [];
    docs.push(`${"=".repeat(80)}\nCOMPANEZE BLDR - BUSINESS DOCUMENT FOLDER\nClient: ${selectedInterview.client_name}\nEmail: ${selectedInterview.client_email}\nGenerated: ${new Date().toLocaleDateString()}\n${"=".repeat(80)}\n\n`);
    if (selectedInterview.business_plan?.content) docs.push(`\n\n${"=".repeat(60)}\nDOCUMENT 1: BUSINESS PLAN\n${"=".repeat(60)}\n\n${selectedInterview.business_plan.content}`);
    if (selectedInterview.ai_research?.content) docs.push(`\n\n${"=".repeat(60)}\nDOCUMENT 2: MARKET RESEARCH & ANALYSIS\n${"=".repeat(60)}\n\n${selectedInterview.ai_research.content}`);
    if (selectedInterview.admin_guide?.content) docs.push(`\n\n${"=".repeat(60)}\nDOCUMENT 3: ADMIN IMPLEMENTATION GUIDE\n${"=".repeat(60)}\n\n${selectedInterview.admin_guide.content}`);
    if (selectedInterview.action_list?.content) docs.push(`\n\n${"=".repeat(60)}\nDOCUMENT 4: ACTION ITEMS & TO-DO LIST\n${"=".repeat(60)}\n\n${selectedInterview.action_list.content}`);
    if (selectedInterview.client_summary?.content) docs.push(`\n\n${"=".repeat(60)}\nDOCUMENT 5: CLIENT SUMMARY\n${"=".repeat(60)}\n\n${selectedInterview.client_summary.content}`);
    const blob = new Blob([docs.join('')], { type: "text/plain" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `Business_Documents_${selectedInterview.client_name.replace(/\s+/g, "_")}_Complete.txt`; a.click();
  };

  // Reply to message handler
  const handleReplyMessage = async () => {
    if (!selectedMessage || !replyText.trim()) return;
    setSendingReply(true);
    try {
      await axios.post(`${API}/admin/messages/${selectedMessage.id}/reply`, { reply_text: replyText });
      setReplyText("");
      fetchMessages();
      // Refresh selected message
      const res = await axios.get(`${API}/admin/messages/${selectedMessage.id}`);
      setSelectedMessage(res.data);
      alert("Reply sent! Client will receive email notification.");
    } catch (err) {
      alert("Failed to send reply");
    }
    setSendingReply(false);
  };

  const updateMessageStatus = async (messageId, status) => {
    try {
      await axios.patch(`${API}/admin/messages/${messageId}/status?status=${status}`);
      fetchMessages();
      if (selectedMessage?.id === messageId) {
        setSelectedMessage(prev => ({ ...prev, status }));
      }
    } catch (err) {
      console.error("Failed to update status:", err);
    }
  };

  if (loading) return <div className="min-h-screen bg-blue-900 flex items-center justify-center text-white">Loading...</div>;

  // Messages Panel Component
  const MessagesPanel = () => (
    <div className="flex flex-col lg:flex-row gap-4 h-[calc(100vh-150px)]">
      {/* Messages List */}
      <div className="lg:w-1/3 bg-gray-800 rounded-lg overflow-hidden">
        <div className="p-4 border-b border-gray-700">
          <h3 className="text-white font-semibold">Client Messages</h3>
          <p className="text-gray-400 text-xs">{messages.filter(m => m.status === "open").length} open tickets</p>
        </div>
        <div className="overflow-y-auto max-h-[500px]">
          {messages.length === 0 ? (
            <p className="text-gray-400 text-sm p-4">No messages yet</p>
          ) : (
            messages.map(msg => (
              <div
                key={msg.id}
                onClick={() => setSelectedMessage(msg)}
                className={`p-4 border-b border-gray-700 cursor-pointer hover:bg-gray-700 ${selectedMessage?.id === msg.id ? "bg-gray-700" : ""}`}
              >
                <div className="flex justify-between items-start mb-1">
                  <span className="text-white font-medium text-sm truncate">{msg.client_name}</span>
                  <span className={`px-2 py-0.5 rounded text-xs ${msg.status === "resolved" ? "bg-green-500/20 text-green-400" : msg.status === "in_progress" ? "bg-blue-500/20 text-blue-400" : "bg-yellow-500/20 text-yellow-400"}`}>
                    {msg.status}
                  </span>
                </div>
                <p className="text-gray-300 text-sm truncate">{msg.subject}</p>
                <p className="text-gray-500 text-xs mt-1">{new Date(msg.created_at).toLocaleDateString()}</p>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Message Detail */}
      <div className="lg:w-2/3 bg-gray-800 rounded-lg p-4 flex flex-col">
        {!selectedMessage ? (
          <div className="flex items-center justify-center h-full text-gray-400">Select a message to view</div>
        ) : (
          <>
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-white font-semibold text-lg">{selectedMessage.subject}</h3>
                <p className="text-gray-400 text-sm">{selectedMessage.client_name} • {selectedMessage.client_email}</p>
              </div>
              <select
                value={selectedMessage.status}
                onChange={(e) => updateMessageStatus(selectedMessage.id, e.target.value)}
                className="bg-gray-700 text-white text-sm rounded px-2 py-1"
              >
                <option value="open">Open</option>
                <option value="in_progress">In Progress</option>
                <option value="resolved">Resolved</option>
                <option value="closed">Closed</option>
              </select>
            </div>

            {/* Conversation Thread */}
            <div className="flex-1 overflow-y-auto space-y-4 mb-4 max-h-[300px]">
              {/* Original Message */}
              <div className="bg-gray-700/50 rounded-lg p-4">
                <p className="text-blue-300 text-xs font-medium mb-1">Client Message</p>
                <p className="text-white text-sm whitespace-pre-wrap">{selectedMessage.message}</p>
                <p className="text-gray-500 text-xs mt-2">{new Date(selectedMessage.created_at).toLocaleString()}</p>
              </div>

              {/* Replies */}
              {selectedMessage.replies?.map(reply => (
                <div key={reply.id} className={`rounded-lg p-4 ${reply.from === "admin" ? "bg-blue-600/20 border-l-2 border-blue-500" : "bg-gray-700/50"}`}>
                  <p className={`text-xs font-medium mb-1 ${reply.from === "admin" ? "text-blue-300" : "text-gray-400"}`}>
                    {reply.from === "admin" ? "Admin Reply" : "Client"}
                  </p>
                  <p className="text-white text-sm whitespace-pre-wrap">{reply.text}</p>
                  <p className="text-gray-500 text-xs mt-2">{new Date(reply.created_at).toLocaleString()}</p>
                </div>
              ))}
            </div>

            {/* Reply Input */}
            <div className="border-t border-gray-700 pt-4">
              <textarea
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                placeholder="Type your reply..."
                className="w-full px-3 py-2 rounded-lg bg-gray-700 text-white text-sm min-h-[80px] mb-2"
              />
              <button
                onClick={handleReplyMessage}
                disabled={sendingReply || !replyText.trim()}
                className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white px-4 py-2 rounded-lg text-sm"
              >
                {sendingReply ? "Sending..." : "Send Reply"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );

  // Interview Detail Component (used in both desktop sidebar and mobile modal)
  const InterviewDetail = () => (
    <div>
      <div className="bg-gray-800 rounded-lg p-4 sm:p-6 mb-6">
        {/* Header with Back & Close buttons */}
        <div className="flex justify-between items-start mb-4">
          <div className="flex items-center gap-3">
            {/* Back Button */}
            <button 
              onClick={() => { setSelectedInterview(null); closeMobileDetail(); }}
              className="p-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg"
              title="Back to list"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
              </svg>
            </button>
            <div className="flex-1 min-w-0">
              <h2 className="text-xl sm:text-2xl font-bold text-white truncate">{selectedInterview.client_name}</h2>
              <p className="text-gray-400 text-sm truncate">{selectedInterview.client_email}</p>
              {selectedInterview.client_phone && <p className="text-gray-500 text-sm">{selectedInterview.client_phone}</p>}
              <p className="text-gray-500 text-sm mt-1">{selectedInterview.answers?.length || 0} answers submitted</p>
            </div>
          </div>
          <div className="flex items-center gap-2 ml-2">
            <span className={`px-2 py-1 rounded-full text-xs text-white whitespace-nowrap ${selectedInterview.status === "completed" ? "bg-green-500" : selectedInterview.status === "processing" ? "bg-yellow-500" : "bg-gray-500"}`}>{selectedInterview.status}</span>
            {/* Edit Mode Toggle */}
            <button 
              onClick={() => setEditMode(!editMode)}
              className={`p-2 ${editMode ? "bg-blue-600" : "bg-gray-700"} hover:bg-gray-600 text-white rounded-lg`}
              title="Edit Mode"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
              </svg>
            </button>
            {/* Mobile close button */}
            <button 
              onClick={closeMobileDetail} 
              className="lg:hidden p-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg"
              aria-label="Close"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </div>

        {/* Admin Notes Section */}
        {editMode && (
          <div className="bg-gray-700/50 rounded-lg p-4 mb-4">
            <label className="text-white font-medium text-sm block mb-2">Admin Notes (Internal Only)</label>
            <textarea
              value={editNotes}
              onChange={(e) => setEditNotes(e.target.value)}
              placeholder="Add internal notes about this client..."
              className="w-full px-3 py-2 rounded-lg bg-gray-700 text-white text-sm min-h-[80px]"
            />
            <button className="mt-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm">Save Notes</button>
          </div>
        )}
        
        {/* Generate All Documents Button */}
        <div className="mb-4">
          <button onClick={() => generateAllDocs(selectedInterview.id)} disabled={generatingAll} className="w-full px-4 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 disabled:from-blue-800 disabled:to-purple-800 text-white rounded-lg font-medium text-sm">
            {generatingAll ? "⏳ Generating Premium Documents..." : "🚀 Generate Premium Business Plan"}
          </button>
        </div>

        {/* Generation Actions */}
        <div className="border-t border-gray-700 pt-4 mt-4">
          <h3 className="text-white font-medium mb-3 text-sm">Generate Individual Documents</h3>
          <div className="flex flex-wrap gap-2 mb-4">
            {selectedInterview.status !== "completed" && <button onClick={() => generatePlan(selectedInterview.id)} disabled={generating} className="px-3 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-800 text-white rounded-lg text-xs sm:text-sm">{generating ? "..." : "Full Plan"}</button>}
            <button onClick={() => generateSummary(selectedInterview.id)} className="px-3 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-xs sm:text-sm">Client Summary</button>
            <button onClick={() => generateAdminGuide(selectedInterview.id)} disabled={generatingGuide} className="px-3 py-2 bg-orange-600 hover:bg-orange-700 disabled:bg-orange-800 text-white rounded-lg text-xs sm:text-sm">{generatingGuide ? "..." : "Implementation Guide"}</button>
            <button onClick={() => generateActionList(selectedInterview.id)} disabled={generatingActions} className="px-3 py-2 bg-teal-600 hover:bg-teal-700 disabled:bg-teal-800 text-white rounded-lg text-xs sm:text-sm">{generatingActions ? "..." : "Action Checklist"}</button>
          </div>
        </div>

        {/* Download & Send Actions */}
        <div className="border-t border-gray-700 pt-4 mt-4">
          <h3 className="text-white font-medium mb-3 text-sm">📁 Premium Document Folder</h3>
          
          {/* Premium PDF Actions */}
          {selectedInterview.business_plan && (
            <div className="bg-gradient-to-r from-amber-900/30 to-yellow-900/30 rounded-lg p-4 mb-4 border border-amber-500/30">
              <h4 className="text-amber-300 font-medium text-sm mb-3">Executive Business Plan</h4>
              <div className="flex flex-wrap gap-2">
                <a 
                  href={`${API}/admin/interviews/${selectedInterview.id}/plan-preview`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-white rounded-lg text-sm font-medium flex items-center gap-2"
                >
                  👁️ Preview Plan
                </a>
                <a 
                  href={`${API}/admin/interviews/${selectedInterview.id}/plan-pdf`}
                  className="px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-lg text-sm font-medium flex items-center gap-2"
                >
                  📄 Download PDF
                </a>
              </div>
            </div>
          )}
          
          {/* Investment Analytics Document */}
          <div className="bg-gradient-to-r from-emerald-900/30 to-teal-900/30 rounded-lg p-4 mb-4 border border-emerald-500/30">
            <h4 className="text-emerald-300 font-medium text-sm mb-2">📊 Investment Analytics Document</h4>
            <p className="text-white/60 text-xs mb-3">Separate document with financial projections, risk analysis, and legal disclaimers for investors.</p>
            <div className="flex flex-wrap gap-2">
              <button 
                onClick={async () => {
                  try {
                    await axios.post(`${API}/admin/interviews/${selectedInterview.id}/generate-investment-analytics`);
                    alert("Investment Analytics generated!");
                    viewInterview(selectedInterview.id);
                  } catch(e) { alert("Failed to generate"); }
                }}
                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium"
              >
                📈 Generate Analytics
              </button>
              {selectedInterview.investment_analytics && (
                <a 
                  href={`${API}/admin/interviews/${selectedInterview.id}/investment-analytics-preview`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white rounded-lg text-sm font-medium flex items-center gap-2"
                >
                  👁️ Preview Analytics
                </a>
              )}
            </div>
          </div>
          
          <div className="flex flex-wrap gap-2 mb-4">
            <button onClick={downloadAllDocs} className="px-3 py-2 bg-gradient-to-r from-green-600 to-teal-600 hover:from-green-700 hover:to-teal-700 text-white rounded-lg text-xs sm:text-sm font-medium">📦 Download Text Package</button>
            {selectedInterview.business_plan && <button onClick={downloadPlan} className="px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-xs sm:text-sm">Plan (TXT)</button>}
            {selectedInterview.admin_guide && <button onClick={downloadAdminGuide} className="px-3 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg text-xs sm:text-sm">Implementation</button>}
            {selectedInterview.action_list && <button onClick={downloadActionList} className="px-3 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg text-xs sm:text-sm">Actions</button>}
          </div>
          {/* Send Documents Section */}
          <div className="mb-4">
            <button 
              onClick={() => setShowSendModal(true)} 
              disabled={!selectedInterview.business_plan}
              className="w-full px-4 py-3 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 disabled:from-gray-600 disabled:to-gray-700 text-white rounded-lg font-medium text-sm"
            >
              📧 Send Documents to Client
            </button>
          </div>
          
          <button onClick={() => deleteInterview(selectedInterview.id)} className="w-full px-3 py-2 bg-red-600/20 hover:bg-red-600/40 text-red-400 rounded-lg text-xs sm:text-sm border border-red-600/30">🗑️ Delete Interview</button>
        </div>

        {/* Client Portal Access Info */}
        <div className="p-3 bg-gradient-to-r from-blue-900/50 to-purple-900/50 rounded-lg mt-4 border border-blue-500/20">
          <div className="flex justify-between items-center mb-2">
            <p className="text-white font-medium text-sm">Client Portal Access</p>
            {selectedInterview.access_code && (
              <span className="bg-amber-500/20 text-amber-300 px-2 py-1 rounded text-xs font-mono">{selectedInterview.access_code}</span>
            )}
          </div>
          <p className="text-gray-400 text-xs mb-2">Knowledge Center Link:</p>
          <code className="text-blue-400 text-xs break-all block bg-black/20 p-2 rounded">{window.location.origin}/knowledge/{selectedInterview.id}</code>
        </div>
      </div>

      {/* Document Tabs */}
      <div className="flex flex-wrap gap-2 mb-4">
        <button onClick={() => setView("detail")} className={`px-3 py-2 rounded-lg text-xs sm:text-sm ${view === "detail" ? "bg-blue-600 text-white" : "bg-gray-700 text-gray-300 hover:bg-gray-600"}`}>Answers</button>
        {selectedInterview.business_plan && <button onClick={() => setView("plan")} className={`px-3 py-2 rounded-lg text-xs sm:text-sm ${view === "plan" ? "bg-blue-600 text-white" : "bg-gray-700 text-gray-300 hover:bg-gray-600"}`}>Plan</button>}
        {selectedInterview.ai_research && <button onClick={() => setView("research")} className={`px-3 py-2 rounded-lg text-xs sm:text-sm ${view === "research" ? "bg-blue-600 text-white" : "bg-gray-700 text-gray-300 hover:bg-gray-600"}`}>Research</button>}
        {selectedInterview.admin_guide && <button onClick={() => setView("admin")} className={`px-3 py-2 rounded-lg text-xs sm:text-sm ${view === "admin" ? "bg-orange-600 text-white" : "bg-orange-700/50 text-orange-200 hover:bg-orange-700"}`}>Admin</button>}
        {selectedInterview.action_list && <button onClick={() => setView("actions")} className={`px-3 py-2 rounded-lg text-xs sm:text-sm ${view === "actions" ? "bg-teal-600 text-white" : "bg-teal-700/50 text-teal-200 hover:bg-teal-700"}`}>Actions</button>}
        {selectedInterview.client_summary && <button onClick={() => setView("summary")} className={`px-3 py-2 rounded-lg text-xs sm:text-sm ${view === "summary" ? "bg-purple-600 text-white" : "bg-gray-700 text-gray-300 hover:bg-gray-600"}`}>Summary</button>}
      </div>

      {/* Document Content */}
      <div className="bg-gray-800 rounded-lg p-4 sm:p-6 max-h-[50vh] overflow-y-auto">
        {view === "detail" && (
          <div>
            {/* Edit Controls */}
            <div className="flex justify-between items-center mb-4">
              <span className="text-gray-400 text-sm">{selectedInterview.answers?.length || 0} answers</span>
              {editMode ? (
                <div className="flex gap-2">
                  <button onClick={() => setEditMode(false)} className="px-3 py-1 bg-gray-600 hover:bg-gray-500 text-white text-sm rounded">Cancel</button>
                  <button onClick={saveEditedAnswers} disabled={savingAnswers} className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white text-sm rounded disabled:opacity-50">
                    {savingAnswers ? "Saving..." : "Save Changes"}
                  </button>
                </div>
              ) : (
                <button onClick={startEditMode} className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded flex items-center gap-1">
                  ✏️ Edit Answers
                </button>
              )}
            </div>
            {/* Answers List */}
            <div className="space-y-4">
              {selectedInterview.answers?.map((a, i) => (
                <div key={i} className="border-b border-gray-700 pb-3 last:border-0">
                  <div className="text-blue-400 text-xs mb-1">{a.section}</div>
                  <div className="text-white text-xs sm:text-sm font-medium mb-1">Q{i+1}: {a.question}</div>
                  {editMode ? (
                    <textarea
                      value={editedAnswers[a.question_id] !== undefined ? editedAnswers[a.question_id] : (a.answer || '')}
                      onChange={(e) => setEditedAnswers(prev => ({ ...prev, [a.question_id]: e.target.value }))}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white text-sm min-h-[80px]"
                      placeholder="Enter answer..."
                    />
                  ) : (
                    <div className="text-gray-300 text-xs sm:text-sm bg-gray-700/50 rounded p-2">
                      {a.answer || <span className="text-gray-500 italic">No answer</span>}
                    </div>
                  )}
                  {a.mode && a.mode !== "self" && <div className="text-xs text-purple-400 mt-1">Mode: {a.mode}</div>}
                </div>
              ))}
            </div>
          </div>
        )}
        {view === "plan" && selectedInterview.business_plan && <pre className="whitespace-pre-wrap text-gray-300 font-sans text-xs sm:text-sm leading-relaxed">{selectedInterview.business_plan.content}</pre>}
        {view === "research" && selectedInterview.ai_research && <pre className="whitespace-pre-wrap text-gray-300 font-sans text-xs sm:text-sm leading-relaxed">{selectedInterview.ai_research.content}</pre>}
        {view === "admin" && selectedInterview.admin_guide && <pre className="whitespace-pre-wrap text-gray-300 font-sans text-xs sm:text-sm leading-relaxed">{selectedInterview.admin_guide.content}</pre>}
        {view === "actions" && selectedInterview.action_list && <pre className="whitespace-pre-wrap text-gray-300 font-sans text-xs sm:text-sm leading-relaxed">{selectedInterview.action_list.content}</pre>}
        {view === "summary" && selectedInterview.client_summary && <pre className="whitespace-pre-wrap text-gray-300 font-sans text-xs sm:text-sm leading-relaxed">{selectedInterview.client_summary.content}</pre>}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-blue-900">
      {/* Header */}
      <header className="bg-gray-800 border-b border-gray-700 px-4 sm:px-6 py-4">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-white">Companeze Admin</h1>
            <p className="text-gray-400 text-xs sm:text-sm">Business Planner Management</p>
          </div>
          <div className="flex items-center gap-4">
            {/* Tab Navigation */}
            <div className="hidden sm:flex gap-2">
              <button
                onClick={() => setAdminTab("interviews")}
                className={`px-3 py-1.5 rounded-lg text-sm ${adminTab === "interviews" ? "bg-blue-600 text-white" : "bg-gray-700 text-gray-300 hover:bg-gray-600"}`}
              >
                Clients
              </button>
              <button
                onClick={() => { setAdminTab("messages"); fetchMessages(); }}
                className={`px-3 py-1.5 rounded-lg text-sm relative ${adminTab === "messages" ? "bg-blue-600 text-white" : "bg-gray-700 text-gray-300 hover:bg-gray-600"}`}
              >
                Messages
                {messages.filter(m => m.status === "open").length > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full text-xs flex items-center justify-center">
                    {messages.filter(m => m.status === "open").length}
                  </span>
                )}
              </button>
            </div>
            <button onClick={onLogout} className="px-3 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm">Logout</button>
          </div>
        </div>
        {/* Mobile Tab Navigation */}
        <div className="sm:hidden flex gap-2 mt-3">
          <button
            onClick={() => setAdminTab("interviews")}
            className={`flex-1 px-3 py-2 rounded-lg text-sm ${adminTab === "interviews" ? "bg-blue-600 text-white" : "bg-gray-700 text-gray-300"}`}
          >
            Clients
          </button>
          <button
            onClick={() => { setAdminTab("messages"); fetchMessages(); }}
            className={`flex-1 px-3 py-2 rounded-lg text-sm relative ${adminTab === "messages" ? "bg-blue-600 text-white" : "bg-gray-700 text-gray-300"}`}
          >
            Messages
            {messages.filter(m => m.status === "open").length > 0 && (
              <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full text-xs flex items-center justify-center">
                {messages.filter(m => m.status === "open").length}
              </span>
            )}
          </button>
        </div>
      </header>

      {/* Messages Tab */}
      {adminTab === "messages" && (
        <div className="p-4">
          <MessagesPanel />
        </div>
      )}

      {/* Interviews Tab - Desktop Layout */}
      {adminTab === "interviews" && (
        <>
          <div className="hidden lg:flex">
            <aside className="w-80 bg-gray-800 min-h-[calc(100vh-73px)] border-r border-gray-700 overflow-y-auto p-4">
              <h2 className="text-lg font-semibold text-white mb-4">Client Consultations</h2>
              {interviews.length === 0 ? <p className="text-gray-400 text-sm">No interviews yet</p> : (
                <div className="space-y-2">
                  {interviews.map(i => (
                    <div key={i.id} onClick={() => viewInterview(i.id)} className={`p-3 rounded-lg cursor-pointer ${selectedInterview?.id === i.id ? "bg-blue-600" : "bg-gray-700 hover:bg-gray-600"}`}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-white font-medium truncate">{i.client_name}</span>
                        <span className={`w-2 h-2 rounded-full flex-shrink-0 ${i.status === "completed" ? "bg-green-500" : i.status === "processing" ? "bg-yellow-500" : "bg-gray-500"}`}></span>
                      </div>
                      <div className="text-gray-400 text-xs truncate">{i.client_email}</div>
                      <div className="text-gray-500 text-xs mt-1">{i.answers?.length || 0} responses</div>
                    </div>
                  ))}
                </div>
              )}
            </aside>
            <main className="flex-1 p-6 overflow-y-auto max-h-[calc(100vh-73px)]">
              {!selectedInterview ? (
                <div className="flex items-center justify-center h-full text-gray-400">Select an interview to view details</div>
              ) : (
                <InterviewDetail />
              )}
            </main>
          </div>

          {/* Mobile Layout - List View */}
          <div className="lg:hidden p-4">
            <h2 className="text-lg font-semibold text-white mb-4">Client Consultations</h2>
            {interviews.length === 0 ? <p className="text-gray-400 text-sm">No interviews yet</p> : (
              <div className="space-y-2">
                {interviews.map(i => (
                  <div key={i.id} onClick={() => viewInterview(i.id)} className="p-4 bg-gray-800 rounded-lg cursor-pointer hover:bg-gray-700 active:bg-gray-600">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-white font-medium">{i.client_name}</span>
                      <span className={`px-2 py-1 rounded-full text-xs text-white ${i.status === "completed" ? "bg-green-500" : i.status === "processing" ? "bg-yellow-500" : "bg-gray-500"}`}>{i.status}</span>
                    </div>
                    <div className="text-gray-400 text-sm">{i.client_email}</div>
                    <div className="text-gray-500 text-xs mt-1">{i.answers?.length || 0} responses • Tap to view</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Mobile Modal for Interview Detail */}
          {showMobileDetail && selectedInterview && (
            <div className="lg:hidden fixed inset-0 bg-blue-900 z-50 overflow-y-auto">
              {/* Modal Header with Close Button */}
              <div className="sticky top-0 bg-gray-800 border-b border-gray-700 px-4 py-3 flex justify-between items-center z-10">
                <h2 className="text-lg font-bold text-white truncate">{selectedInterview.client_name}</h2>
                <button 
                  onClick={closeMobileDetail} 
                  className="p-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg flex items-center gap-2"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                  <span className="text-sm">Close</span>
                </button>
              </div>
              {/* Modal Content */}
              <div className="p-4">
                <InterviewDetail />
              </div>
              {/* Fixed Bottom Close Button for extra accessibility */}
              <div className="sticky bottom-0 p-4 bg-blue-900 border-t border-gray-700">
                <button 
                  onClick={closeMobileDetail} 
                  className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium"
                >
                  ← Back to Client List
                </button>
              </div>
            </div>
          )}
        </>
      )}
      
      {/* Send Modal */}
          {showSendModal && (
            <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
              <div className="bg-gray-800 rounded-xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
                <h3 className="text-xl font-bold text-white mb-4">Send to {selectedInterview.client_name}</h3>
                
                {/* Client Access Info - Always Visible */}
                <div className="bg-gradient-to-r from-blue-900/50 to-purple-900/50 rounded-lg p-4 mb-4 border border-blue-500/30">
                  <h4 className="text-amber-400 font-semibold mb-2">📋 Client Access Information</h4>
                  <div className="space-y-2 text-sm">
                    <p><span className="text-gray-400">Email:</span> <span className="text-white">{selectedInterview.client_email}</span></p>
                    <p><span className="text-gray-400">Access Code:</span> <span className="font-mono text-xl text-amber-400 bg-black/30 px-2 py-1 rounded">{selectedInterview.access_code || 'N/A'}</span></p>
                    <p><span className="text-gray-400">Portal:</span> <span className="text-blue-400">{window.location.origin}/portal</span></p>
                  </div>
                </div>
                
                {/* Quick Copy Buttons */}
                <div className="grid grid-cols-2 gap-2 mb-4">
                  <button 
                    onClick={() => { navigator.clipboard.writeText(selectedInterview.access_code || ''); alert('Access code copied!'); }}
                    className="p-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-sm"
                  >
                    📋 Copy Access Code
                  </button>
                  <button 
                    onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/portal`); alert('Portal link copied!'); }}
                    className="p-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm"
                  >
                    🔗 Copy Portal Link
                  </button>
                </div>
                
                <div className="space-y-3 mb-4">
                  <button 
                    onClick={() => handleSendToClient('email')}
                    disabled={sendingToClient}
                    className="w-full p-4 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white rounded-lg text-left"
                  >
                    <div className="font-semibold">📧 Send via Email</div>
                    <div className="text-sm text-blue-200">{selectedInterview.client_email}</div>
                    <div className="text-xs text-blue-300 mt-1">Sends professional email with access info</div>
                  </button>
                  
                  <button 
                    onClick={() => handleSendToClient('sms')}
                    disabled={sendingToClient || !selectedInterview.client_phone}
                    className="w-full p-4 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 text-white rounded-lg text-left"
                  >
                    <div className="font-semibold">📱 Send via SMS</div>
                    <div className="text-sm text-green-200">{selectedInterview.client_phone || 'No phone number'}</div>
                  </button>
                  
                  <button 
                    onClick={() => handleSendToClient('link')}
                    disabled={sendingToClient}
                    className="w-full p-4 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-600 text-white rounded-lg text-left"
                  >
                    <div className="font-semibold">🔗 Copy Full Message</div>
                    <div className="text-sm text-purple-200">Copy complete message with all details to clipboard</div>
                  </button>
                </div>
                
                {shareInfo && (
                  <div className="bg-gray-700 rounded-lg p-4 mb-4">
                    <p className="text-green-400 font-semibold mb-2">✓ {shareInfo.message}</p>
                    {shareInfo.share_text && (
                      <div className="bg-black/30 p-3 rounded text-sm text-gray-300 whitespace-pre-wrap max-h-40 overflow-y-auto">
                        {shareInfo.share_text}
                      </div>
                    )}
                  </div>
                )}
                
                <div className="flex gap-2">
                  <button onClick={() => { setShowSendModal(false); setShareInfo(null); }} className="flex-1 px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded-lg">
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}
    </div>
  );
};

// Admin Login
const AdminLogin = ({ onLogin }) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => { e.preventDefault(); try { const res = await axios.post(`${API}/admin/login`, { username, password }); if (res.data.success) { localStorage.setItem("adminToken", res.data.token); onLogin(); } } catch(e) { setError("Invalid credentials"); } };

  return (
    <div className="min-h-screen bg-blue-900 flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        <a href="https://companeze.com" className="flex items-center justify-center gap-2 mb-6 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-white font-medium transition-colors mx-auto w-fit">
          ← Back to Companeze.com
        </a>
        <h1 className="text-3xl font-bold text-white text-center mb-8">Companeze Admin</h1>
        <form onSubmit={handleSubmit} className="bg-gray-800 rounded-lg p-8">
          {error && <div className="bg-red-500/20 text-red-400 px-4 py-2 rounded mb-4">{error}</div>}
          <div className="mb-4"><label className="block text-gray-300 mb-2">Username</label><input type="text" value={username} onChange={(e) => setUsername(e.target.value)} className="w-full px-4 py-3 rounded bg-gray-700 text-white" /></div>
          <div className="mb-6">
            <label className="block text-gray-300 mb-2">Password</label>
            <div className="relative">
              <input 
                type={showPassword ? "text" : "password"} 
                value={password} 
                onChange={(e) => setPassword(e.target.value)} 
                className="w-full px-4 py-3 rounded bg-gray-700 text-white pr-12" 
              />
              <button 
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white text-sm"
              >
                {showPassword ? "🙈 Hide" : "👁️ Show"}
              </button>
            </div>
          </div>
          <button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded">Sign In</button>
        </form>
      </div>
    </div>
  );
};

// Funding Services
const FundingServices = () => {
  const [tiers, setTiers] = useState([]);
  const [selectedTier, setSelectedTier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({ name: "", email: "", phone: "", capitalNeeded: "", fundingPurpose: "", timeline: "", notes: "" });
  const [accessCode, setAccessCode] = useState("");
  const [accessGranted, setAccessGranted] = useState(false);
  const [accessLevel, setAccessLevel] = useState(null); // '199', '599', 'all', 'custom'
  const [codeError, setCodeError] = useState("");

  useEffect(() => { 
    // Check if already has access from URL or session
    const urlParams = new URLSearchParams(window.location.search);
    const codeFromUrl = urlParams.get('code');
    if (codeFromUrl) {
      validateCode(codeFromUrl);
    }
    axios.get(`${API}/pricing`).then(res => { setTiers(res.data.service_tiers || []); setLoading(false); }).catch(() => setLoading(false)); 
  }, []);

  const validateCode = (code) => {
    const upperCode = code.toUpperCase();
    if (upperCode === 'FUND199') {
      setAccessGranted(true);
      setAccessLevel('199');
      setCodeError("");
    } else if (upperCode === 'FUND599') {
      setAccessGranted(true);
      setAccessLevel('599');
      setCodeError("");
    } else if (upperCode === 'FUNDALL') {
      setAccessGranted(true);
      setAccessLevel('all');
      setCodeError("");
    } else if (upperCode.startsWith('CUSTOM')) {
      setAccessGranted(true);
      setAccessLevel('custom');
      setCodeError("");
    } else {
      setCodeError("Invalid access code. Please contact Companeze for access.");
    }
  };

  const handleCodeSubmit = (e) => {
    e.preventDefault();
    validateCode(accessCode);
  };

  const getVisibleTiers = () => {
    if (!accessGranted) return [];
    if (accessLevel === 'all') return tiers;
    if (accessLevel === '199') return tiers.filter(t => t.id === 'starter');
    if (accessLevel === '599') return tiers.filter(t => t.id === 'starter' || t.id === 'growth');
    if (accessLevel === 'custom') return tiers.filter(t => t.id === 'custom');
    return [];
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedTier) { alert("Please select a service tier"); return; }
    setSubmitting(true);
    try { await axios.post(`${API}/funding-request`, { client_name: formData.name, client_email: formData.email, client_phone: formData.phone, service_tier: selectedTier.id, capital_needed: formData.capitalNeeded, funding_purpose: formData.fundingPurpose, timeline: formData.timeline, additional_notes: formData.notes, access_code: accessCode }); setSubmitted(true); } catch(e) { alert("Failed to submit"); }
    setSubmitting(false);
  };

  if (loading) return <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900"><Header /><div className="flex items-center justify-center py-20 text-white">Loading funding services...</div></div>;

  // Access Code Login Screen
  if (!accessGranted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
        <Header showShare={false} />
        <div className="flex items-center justify-center py-20 px-4">
          <div className="max-w-md w-full">
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-white mb-2">Companeze BLDR</h1>
              <p className="text-blue-200">Funding Services Access</p>
            </div>
            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 shadow-2xl">
              <h2 className="text-xl font-semibold text-white mb-4">Enter Access Code</h2>
              <p className="text-white/70 text-sm mb-6">Please enter your funding access code to view available services.</p>
              <form onSubmit={handleCodeSubmit} className="space-y-4">
                <div>
                  <input
                    type="text"
                    value={accessCode}
                    onChange={(e) => setAccessCode(e.target.value)}
                    placeholder="Enter access code"
                    className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/50 text-center text-lg tracking-wider"
                  />
                </div>
                {codeError && <p className="text-red-400 text-sm text-center">{codeError}</p>}
                <button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-lg">
                  Access Funding
                </button>
              </form>
              <div className="mt-6 pt-6 border-t border-white/10 text-center">
                <p className="text-white/50 text-sm">Don't have an access code?</p>
                <a href="/" className="text-blue-400 hover:text-blue-300 text-sm">Start with Business Consultation</a>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (submitted) return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900"><Header />
      <div className="flex items-center justify-center py-12 px-4"><div className="max-w-2xl mx-auto text-center"><div className="bg-white/10 backdrop-blur-lg rounded-2xl p-12 shadow-2xl">
        <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6"><svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg></div>
        <h2 className="text-3xl font-bold text-white mb-4">Funding Request Submitted!</h2>
        <p className="text-xl text-blue-200 mb-6">Our team will contact you within 24-48 hours.</p>
        <div className="bg-white/5 rounded-lg p-4 text-left"><p className="text-white/60 text-sm">Service: <span className="text-white font-medium">{selectedTier?.name}</span></p><p className="text-white/60 text-sm">Capital: <span className="text-white font-medium">{formData.capitalNeeded}</span></p></div>
      </div></div></div>
    </div>
  );

  const visibleTiers = getVisibleTiers();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900"><Header />
      <div className="py-12 px-4"><div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-3xl font-bold text-white mb-2">Funding Services</h1>
          <p className="text-xl text-blue-200">Capital Raising Solutions</p>
          <p className="text-white/60 mt-2">Flat Rate + 5% Success Fee</p>
          {accessLevel !== 'all' && <p className="text-green-400 text-sm mt-2">Access Level: {accessLevel === '199' ? 'Starter' : accessLevel === '599' ? 'Growth' : 'Custom'}</p>}
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {visibleTiers.map(tier => (
            <div key={tier.id} onClick={() => setSelectedTier(tier)} className={`relative bg-white/10 backdrop-blur-lg rounded-2xl p-6 cursor-pointer transition-all ${selectedTier?.id === tier.id ? "ring-2 ring-blue-400 bg-blue-500/20" : "hover:bg-white/15"}`}>
              {selectedTier?.id === tier.id && <div className="absolute top-4 right-4 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center"><svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg></div>}
              <h3 className="text-xl font-bold text-white mb-2">{tier.name}</h3>
              <div className="text-3xl font-bold text-blue-400 mb-2">{tier.price > 0 ? `$${tier.price.toLocaleString()}` : "Custom"}</div>
              <p className="text-white/60 text-sm mb-2">{tier.capital_range}</p>
              <p className="text-green-400 text-sm mb-4">+ {tier.success_fee}</p>
              <p className="text-white/70 text-sm mb-4">{tier.description}</p>
              <ul className="space-y-2">{tier.features.map((f, i) => <li key={i} className="flex items-start gap-2 text-sm text-white/80"><span className="text-green-400">&#10003;</span><span>{f}</span></li>)}</ul>
            </div>
          ))}
        </div>
        {selectedTier && (
          <div className="max-w-2xl mx-auto"><div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 shadow-2xl">
            <h2 className="text-2xl font-semibold text-white mb-6">Request {selectedTier.name} Package</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div><label className="block text-white mb-2">Full Name *</label><input type="text" required value={formData.name} onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))} className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white" /></div>
                <div><label className="block text-white mb-2">Email *</label><input type="email" required value={formData.email} onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))} className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white" /></div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div><label className="block text-white mb-2">Phone</label><input type="tel" value={formData.phone} onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))} className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white" /></div>
                <div><label className="block text-white mb-2">Capital Needed *</label><input type="text" required placeholder="e.g., $150,000" value={formData.capitalNeeded} onChange={(e) => setFormData(prev => ({ ...prev, capitalNeeded: e.target.value }))} className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/50" /></div>
              </div>
              <div><label className="block text-white mb-2">What will funding be used for? *</label><textarea required value={formData.fundingPurpose} onChange={(e) => setFormData(prev => ({ ...prev, fundingPurpose: e.target.value }))} className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white min-h-[100px]" /></div>
              <div><label className="block text-white mb-2">When do you need funding? *</label><select required value={formData.timeline} onChange={(e) => setFormData(prev => ({ ...prev, timeline: e.target.value }))} className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white"><option value="">Select timeline</option><option value="immediate">Immediate (30 days)</option><option value="1-3months">1-3 months</option><option value="3-6months">3-6 months</option><option value="6+months">6+ months</option></select></div>
              <div><label className="block text-white mb-2">Additional Notes</label><textarea value={formData.notes} onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))} className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white min-h-[80px]" /></div>
              <div className="bg-white/5 rounded-lg p-4"><p className="text-white/80 text-sm"><strong>Service:</strong> {selectedTier.name} - {selectedTier.price > 0 ? `$${selectedTier.price.toLocaleString()}` : "Custom"}</p><p className="text-green-400 text-sm"><strong>Success Fee:</strong> {selectedTier.success_fee}</p></div>
              <button type="submit" disabled={submitting} className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-800 text-white font-semibold py-4 rounded-lg">{submitting ? "Submitting..." : "Submit Funding Request"}</button>
            </form>
          </div></div>
        )}
        <div className="text-center mt-8"><a href="/" className="text-blue-400 hover:text-blue-300">Back to Business Consultation</a></div>
      </div></div>
    </div>
  );
};

// Client Portal Component
const ClientPortal = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [clientData, setClientData] = useState(null);
  const [interviews, setInterviews] = useState([]);
  const [messages, setMessages] = useState([]);
  const [selectedInterview, setSelectedInterview] = useState(null);
  const [activeTab, setActiveTab] = useState("documents");
  const [loading, setLoading] = useState(false);
  const [loginForm, setLoginForm] = useState({ email: "", accessCode: "" });
  const [showAccessCode, setShowAccessCode] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [newMessage, setNewMessage] = useState({ subject: "", message: "" });
  const [sendingMessage, setSendingMessage] = useState(false);
  const [services, setServices] = useState([]);
  const [serviceCategories, setServiceCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [attachmentFile, setAttachmentFile] = useState(null);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setLoginError("");
    try {
      const res = await axios.post(`${API}/client/login`, {
        email: loginForm.email,
        access_code: loginForm.accessCode
      });
      if (res.data.success) {
        setClientData(res.data);
        localStorage.setItem("clientEmail", res.data.email);
        localStorage.setItem("clientName", res.data.client_name);
        setIsLoggedIn(true);
        loadClientData(res.data.email);
      }
    } catch (err) {
      setLoginError("Invalid email or access code. Please try again.");
    }
    setLoading(false);
  };

  const loadClientData = async (email) => {
    try {
      const [interviewsRes, messagesRes, servicesRes] = await Promise.all([
        axios.get(`${API}/client/interviews/${encodeURIComponent(email)}`),
        axios.get(`${API}/client/messages/${encodeURIComponent(email)}`),
        axios.get(`${API}/services`)
      ]);
      setInterviews(interviewsRes.data.interviews || []);
      setMessages(messagesRes.data.messages || []);
      setServices(servicesRes.data.services || []);
      setServiceCategories(servicesRes.data.categories || []);
      if (interviewsRes.data.interviews?.length > 0) {
        loadInterviewDetail(email, interviewsRes.data.interviews[0].id);
      }
    } catch (err) {
      console.error("Error loading client data:", err);
    }
  };

  const loadInterviewDetail = async (email, interviewId) => {
    try {
      const res = await axios.get(`${API}/client/interviews/${encodeURIComponent(email)}/${interviewId}`);
      setSelectedInterview(res.data);
    } catch (err) {
      console.error("Error loading interview:", err);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.subject || !newMessage.message) return;
    setSendingMessage(true);
    try {
      // Use FormData if there's an attachment
      if (attachmentFile) {
        const formData = new FormData();
        formData.append('client_name', clientData.client_name);
        formData.append('client_email', clientData.email);
        formData.append('subject', newMessage.subject);
        formData.append('message_text', newMessage.message);
        if (selectedInterview?.id) formData.append('interview_id', selectedInterview.id);
        formData.append('file', attachmentFile);
        
        await axios.post(`${API}/messages/with-attachment`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } else {
        await axios.post(`${API}/messages`, {
          client_name: clientData.client_name,
          client_email: clientData.email,
          subject: newMessage.subject,
          message: newMessage.message,
          interview_id: selectedInterview?.id
        });
      }
      setNewMessage({ subject: "", message: "" });
      setAttachmentFile(null);
      loadClientData(clientData.email);
      alert("Message sent successfully! We'll respond shortly.");
    } catch (err) {
      alert("Failed to send message. Please try again.");
    }
    setSendingMessage(false);
  };

  const handleServiceInquiry = async (service) => {
    try {
      await axios.post(`${API}/services/inquiry`, {
        client_name: clientData.client_name,
        client_email: clientData.email,
        service_id: service.id
      });
      alert(`Thank you for your interest in ${service.name}! We'll be in touch shortly.`);
    } catch (err) {
      alert("Failed to submit inquiry. Please try again.");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("clientEmail");
    localStorage.removeItem("clientName");
    setIsLoggedIn(false);
    setClientData(null);
  };

  // Check for existing session
  useEffect(() => {
    const savedEmail = localStorage.getItem("clientEmail");
    const savedName = localStorage.getItem("clientName");
    if (savedEmail && savedName) {
      setClientData({ email: savedEmail, client_name: savedName });
      setIsLoggedIn(true);
      loadClientData(savedEmail);
    }
  }, []);

  // Login Screen
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
        <Header showShare={false} />
        <div className="flex items-center justify-center py-12 px-4">
          <div className="max-w-md w-full">
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-white mb-2">Client Portal</h1>
              <p className="text-blue-200">Access your documents and messages</p>
            </div>
            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8">
              <form onSubmit={handleLogin} className="space-y-6">
                <div>
                  <label className="block text-white mb-2">Email Address</label>
                  <input
                    type="email"
                    required
                    value={loginForm.email}
                    onChange={(e) => setLoginForm(prev => ({ ...prev, email: e.target.value }))}
                    className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white"
                    placeholder="Enter your email"
                  />
                </div>
                <div>
                  <label className="block text-white mb-2">Access Code</label>
                  <div className="relative">
                    <input
                      type={showAccessCode ? "text" : "password"}
                      required
                      value={loginForm.accessCode}
                    onChange={(e) => setLoginForm(prev => ({ ...prev, accessCode: e.target.value.toUpperCase() }))}
                    className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white font-mono tracking-widest pr-16"
                    placeholder="Enter 6-digit code"
                    maxLength={6}
                  />
                    <button 
                      type="button"
                      onClick={() => setShowAccessCode(!showAccessCode)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-white/60 hover:text-white text-sm"
                    >
                      {showAccessCode ? "🙈 Hide" : "👁️ Show"}
                    </button>
                  </div>
                  <p className="text-white/50 text-xs mt-2">Your access code was provided when you completed the interview</p>
                </div>
                {loginError && <p className="text-red-400 text-sm">{loginError}</p>}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-800 text-white font-semibold py-4 rounded-lg"
                >
                  {loading ? "Signing In..." : "Sign In"}
                </button>
              </form>
              <div className="mt-6 text-center">
                <p className="text-white/60 text-sm">Don't have an access code?</p>
                <a href="/" className="text-blue-400 hover:text-blue-300 text-sm">Start your business consultation</a>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Portal Dashboard
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
      <Header showShare={false} />
      <div className="max-w-6xl mx-auto py-8 px-4">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-2xl font-bold text-white">Welcome, {clientData?.client_name}</h1>
            <p className="text-blue-200">{clientData?.email}</p>
          </div>
          <button onClick={handleLogout} className="bg-white/10 hover:bg-white/20 text-white px-4 py-2 rounded-lg text-sm">
            Sign Out
          </button>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-6 border-b border-white/10 pb-4">
          <button
            onClick={() => setActiveTab("documents")}
            className={`px-4 py-2 rounded-lg font-medium ${activeTab === "documents" ? "bg-blue-600 text-white" : "bg-white/10 text-white/70 hover:bg-white/20"}`}
          >
            My Documents
          </button>
          <button
            onClick={() => setActiveTab("services")}
            className={`px-4 py-2 rounded-lg font-medium ${activeTab === "services" ? "bg-blue-600 text-white" : "bg-white/10 text-white/70 hover:bg-white/20"}`}
          >
            Services
          </button>
          <button
            onClick={() => setActiveTab("messages")}
            className={`px-4 py-2 rounded-lg font-medium relative ${activeTab === "messages" ? "bg-blue-600 text-white" : "bg-white/10 text-white/70 hover:bg-white/20"}`}
          >
            Messages
            {messages.filter(m => m.status === "open" || m.replies?.length > 0).length > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-xs flex items-center justify-center">
                {messages.filter(m => m.replies?.length > 0).length}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab("contact")}
            className={`px-4 py-2 rounded-lg font-medium ${activeTab === "contact" ? "bg-blue-600 text-white" : "bg-white/10 text-white/70 hover:bg-white/20"}`}
          >
            Contact Us
          </button>
        </div>

        {/* Documents Tab */}
        {activeTab === "documents" && (
          <div className="grid md:grid-cols-3 gap-6">
            {/* Interview List */}
            <div className="bg-white/10 backdrop-blur-lg rounded-xl p-4">
              <h3 className="text-white font-semibold mb-4">Your Projects</h3>
              {interviews.length === 0 ? (
                <p className="text-white/60 text-sm">No projects yet</p>
              ) : (
                <div className="space-y-2">
                  {interviews.map(interview => (
                    <button
                      key={interview.id}
                      onClick={() => loadInterviewDetail(clientData.email, interview.id)}
                      className={`w-full text-left p-3 rounded-lg transition ${selectedInterview?.id === interview.id ? "bg-blue-600" : "bg-white/5 hover:bg-white/10"}`}
                    >
                      <p className="text-white font-medium text-sm">{interview.client_name}</p>
                      <p className="text-white/60 text-xs">{new Date(interview.created_at).toLocaleDateString()}</p>
                      <span className={`inline-block mt-1 px-2 py-0.5 rounded text-xs ${interview.status === "completed" ? "bg-green-500/20 text-green-400" : "bg-yellow-500/20 text-yellow-400"}`}>
                        {interview.status}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Documents */}
            <div className="md:col-span-2 bg-white/10 backdrop-blur-lg rounded-xl p-6">
              <h3 className="text-white font-semibold mb-4">Generated Documents</h3>
              {!selectedInterview ? (
                <p className="text-white/60">Select a project to view documents</p>
              ) : !selectedInterview.all_documents_generated ? (
                <div className="text-center py-8">
                  <div className="animate-pulse text-blue-400 text-4xl mb-4">⏳</div>
                  <p className="text-white">Documents are being generated...</p>
                  <p className="text-white/60 text-sm">Please check back in a few minutes</p>
                </div>
              ) : (
                <div className="grid gap-4">
                  {selectedInterview.documents?.client_summary && (
                    <div className="bg-white/5 rounded-lg p-4">
                      <div className="flex justify-between items-start mb-2">
                        <h4 className="text-white font-medium">Business Summary</h4>
                        <span className="text-green-400 text-xs">Ready</span>
                      </div>
                      <p className="text-white/70 text-sm mb-3">{selectedInterview.documents.client_summary.summary?.substring(0, 200)}...</p>
                      <a href={`/knowledge/${selectedInterview.id}`} className="text-blue-400 hover:text-blue-300 text-sm">View Full Summary →</a>
                    </div>
                  )}
                  {selectedInterview.documents?.business_plan && (
                    <div className="bg-white/5 rounded-lg p-4">
                      <div className="flex justify-between items-start mb-2">
                        <h4 className="text-white font-medium">Business Plan</h4>
                        <span className="text-green-400 text-xs">Ready</span>
                      </div>
                      <p className="text-white/70 text-sm">Your comprehensive investment-grade business plan</p>
                    </div>
                  )}
                  {selectedInterview.documents?.action_list && (
                    <div className="bg-white/5 rounded-lg p-4">
                      <div className="flex justify-between items-start mb-2">
                        <h4 className="text-white font-medium">Action Items & To-Do List</h4>
                        <span className="text-green-400 text-xs">Ready</span>
                      </div>
                      <p className="text-white/70 text-sm">Prioritized checklist to launch your business</p>
                    </div>
                  )}
                  {selectedInterview.documents?.ai_research && (
                    <div className="bg-white/5 rounded-lg p-4">
                      <div className="flex justify-between items-start mb-2">
                        <h4 className="text-white font-medium">Market Research</h4>
                        <span className="text-green-400 text-xs">Ready</span>
                      </div>
                      <p className="text-white/70 text-sm">Industry analysis and market data</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Messages Tab */}
        {activeTab === "messages" && (
          <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6">
            <h3 className="text-white font-semibold mb-4">Your Messages</h3>
            {messages.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-white/60 mb-4">No messages yet</p>
                <button onClick={() => setActiveTab("contact")} className="text-blue-400 hover:text-blue-300">
                  Send us a message →
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {messages.map(msg => (
                  <div key={msg.id} className="bg-white/5 rounded-lg p-4">
                    <div className="flex justify-between items-start mb-2">
                      <h4 className="text-white font-medium">{msg.subject}</h4>
                      <span className={`px-2 py-0.5 rounded text-xs ${msg.status === "resolved" ? "bg-green-500/20 text-green-400" : msg.status === "in_progress" ? "bg-blue-500/20 text-blue-400" : "bg-yellow-500/20 text-yellow-400"}`}>
                        {msg.status}
                      </span>
                    </div>
                    <p className="text-white/70 text-sm mb-2">{msg.message}</p>
                    <p className="text-white/40 text-xs">{new Date(msg.created_at).toLocaleString()}</p>
                    
                    {/* Replies */}
                    {msg.replies?.length > 0 && (
                      <div className="mt-4 border-t border-white/10 pt-4 space-y-3">
                        {msg.replies.map(reply => (
                          <div key={reply.id} className="bg-blue-500/10 rounded-lg p-3 border-l-2 border-blue-500">
                            <p className="text-blue-300 text-xs font-medium mb-1">Companeze Team</p>
                            <p className="text-white/90 text-sm">{reply.text}</p>
                            <p className="text-white/40 text-xs mt-1">{new Date(reply.created_at).toLocaleString()}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Contact Tab */}
        {activeTab === "contact" && (
          <div className="max-w-2xl mx-auto bg-white/10 backdrop-blur-lg rounded-xl p-6">
            <h3 className="text-white font-semibold mb-4">Send Us a Message</h3>
            <form onSubmit={handleSendMessage} className="space-y-4">
              <div>
                <label className="block text-white mb-2">Subject</label>
                <input
                  type="text"
                  required
                  value={newMessage.subject}
                  onChange={(e) => setNewMessage(prev => ({ ...prev, subject: e.target.value }))}
                  className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white"
                  placeholder="What can we help you with?"
                />
              </div>
              <div>
                <label className="block text-white mb-2">Message</label>
                <textarea
                  required
                  value={newMessage.message}
                  onChange={(e) => setNewMessage(prev => ({ ...prev, message: e.target.value }))}
                  className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white min-h-[150px]"
                  placeholder="Tell us more..."
                />
              </div>
              <div>
                <label className="block text-white mb-2">Attach File (optional)</label>
                <input
                  type="file"
                  onChange={(e) => setAttachmentFile(e.target.files[0])}
                  className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-blue-600 file:text-white file:cursor-pointer"
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.txt,.csv,.xlsx"
                />
                {attachmentFile && (
                  <p className="text-green-400 text-sm mt-2">Selected: {attachmentFile.name}</p>
                )}
                <p className="text-white/50 text-xs mt-1">Max 10MB. Allowed: PDF, DOC, DOCX, Images, TXT, CSV, XLSX</p>
              </div>
              <button
                type="submit"
                disabled={sendingMessage}
                className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-800 text-white font-semibold py-3 rounded-lg"
              >
                {sendingMessage ? "Sending..." : "Send Message"}
              </button>
            </form>
            <div className="mt-6 p-4 bg-white/5 rounded-lg">
              <p className="text-white/60 text-sm">
                We typically respond within 24 hours. You will receive an email notification when we reply.
              </p>
            </div>
          </div>
        )}

        {/* Services Tab */}
        {activeTab === "services" && (
          <div>
            {/* Featured Services Banner */}
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl p-6 mb-6">
              <h3 className="text-2xl font-bold text-white mb-2">Business Services Marketplace</h3>
              <p className="text-white/80">Everything you need to launch and grow your business</p>
            </div>

            {/* Category Filter */}
            <div className="flex flex-wrap gap-2 mb-6">
              <button
                onClick={() => setSelectedCategory("all")}
                className={`px-4 py-2 rounded-lg text-sm ${selectedCategory === "all" ? "bg-blue-600 text-white" : "bg-white/10 text-white/70 hover:bg-white/20"}`}
              >
                All Services
              </button>
              {serviceCategories.map(cat => (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategory(cat.id)}
                  className={`px-4 py-2 rounded-lg text-sm ${selectedCategory === cat.id ? "bg-blue-600 text-white" : "bg-white/10 text-white/70 hover:bg-white/20"}`}
                >
                  {cat.name}
                </button>
              ))}
            </div>

            {/* Services Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {services
                .filter(s => selectedCategory === "all" || s.category === selectedCategory)
                .map(service => (
                  <div key={service.id} className={`bg-white/10 backdrop-blur-lg rounded-xl p-5 ${service.featured ? "ring-2 ring-amber-400" : ""}`}>
                    {service.featured && (
                      <span className="inline-block bg-amber-500 text-white text-xs px-2 py-1 rounded mb-3">Featured</span>
                    )}
                    <h4 className="text-white font-semibold text-lg mb-1">{service.name}</h4>
                    <p className="text-blue-300 text-sm mb-2">{service.provider}</p>
                    <p className="text-white/70 text-sm mb-4">{service.description}</p>
                    
                    <div className="flex items-baseline gap-1 mb-4">
                      {service.price === 0 ? (
                        <span className="text-green-400 font-semibold">Free to start</span>
                      ) : (
                        <>
                          <span className="text-2xl font-bold text-white">${service.price}</span>
                          <span className="text-white/60 text-sm">/{service.price_type === "monthly" ? "mo" : service.price_type === "one-time" ? "one-time" : ""}</span>
                        </>
                      )}
                      {service.price_note && <span className="text-white/50 text-xs ml-2">{service.price_note}</span>}
                    </div>

                    {service.is_direct ? (
                      <button
                        onClick={() => handleServiceInquiry(service)}
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-lg text-sm font-medium"
                      >
                        Request Service
                      </button>
                    ) : (
                      <div className="flex gap-2">
                        <a
                          href={service.affiliate_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex-1 bg-green-600 hover:bg-green-700 text-white py-2 rounded-lg text-sm font-medium text-center"
                        >
                          Get Started
                        </a>
                        <button
                          onClick={() => handleServiceInquiry(service)}
                          className="px-4 bg-white/10 hover:bg-white/20 text-white py-2 rounded-lg text-sm"
                        >
                          Help
                        </button>
                      </div>
                    )}
                  </div>
                ))}
            </div>

            {/* Custom Services CTA */}
            <div className="mt-8 bg-white/5 rounded-xl p-6 text-center">
              <h4 className="text-white font-semibold mb-2">Need Something Custom?</h4>
              <p className="text-white/60 text-sm mb-4">Our team can help you find the right solutions for your business</p>
              <button
                onClick={() => setActiveTab("contact")}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg"
              >
                Contact Us
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Main App
function App() {
  const path = window.location.pathname;
  const isAdmin = path === "/admin" || path.startsWith("/admin");
  const isKnowledge = path.startsWith("/knowledge/");
  const isFunding = path === "/funding";
  const isTerms = path === "/terms";
  const isPricing = path === "/pricing";
  const isPayments = path === "/payments";
  const isPaymentSuccess = path === "/payment-success";
  const isPortal = path === "/portal";
  const knowledgeId = isKnowledge ? path.split("/knowledge/")[1] : null;
  const [isLoggedIn, setIsLoggedIn] = useState(() => isAdmin ? !!localStorage.getItem("adminToken") : false);

  if (isTerms) return <TermsPage />;
  if (isPricing) return <PricingPage />;
  if (isPayments) return <PaymentsPage />;
  if (isPaymentSuccess) return <PaymentSuccessPage />;
  if (isFunding) return <FundingServices />;
  if (isPortal) return <ClientPortal />;
  if (isKnowledge && knowledgeId) return <KnowledgeCenter interviewId={knowledgeId} />;
  if (isAdmin) {
    if (!isLoggedIn) return <AdminLogin onLogin={() => setIsLoggedIn(true)} />;
    return <AdminDashboard onLogout={() => { localStorage.removeItem("adminToken"); setIsLoggedIn(false); }} />;
  }
  return <ClientInterview />;
}

export default App;
